// Shared report-data layer for SMJ Financial Portal.
//
// Single source of truth for sign normalization, mapping rollups, validation
// ties, and structured statement data. The Staff Dashboard ReportLayer and the
// printable PrintModal both consume this module so a fix here propagates
// everywhere — no template-fallback chart of accounts, no per-component math.
//
// Conventions (per CPA presentation standards):
//   • Backend Trial Balance returns natural debit-credit signed balances:
//       Assets/Expenses (debit balance) -> positive
//       Liabilities/Equity/Income (credit balance) -> negative
//       Contra accounts keep their opposite sign
//   • SECTION_SIGN below flips credit-natural sections so users see positive
//     numbers in standard CPA presentation (revenue, liabilities, equity).
//   • A line value is genuinely negative ONLY if the natural-sign result is
//     negative after the flip — e.g. a net loss, or a contra account in an
//     asset section. Only those render with parentheses + red.

export const SECTION_SIGN = {
  // Income Statement
  income:        -1,
  cogs:          +1,
  opex:          +1,
  other_income:  -1,
  other_expense: +1,
  // Balance Sheet
  cash:               +1,
  assets_current:     +1,
  assets_fixed:       +1,
  assets_other:       +1,
  liabilities_current: -1,
  debt_short:         -1,
  liabilities_long:   -1,
  equity:             -1,
};

// Round to whole dollars for display.
const round0 = (v) => Math.round(v || 0);

// Largest-remainder rounding. Given a list of unrounded float values and a
// target total (which is round(sum(values))), return a list of rounded
// integers whose sum equals the target exactly. The residual is distributed
// to entries with the largest absolute value first — so the visible "plug"
// lands on the largest line in the section and stays invisible at the cent
// level. No "Rounding adjustment" plug line on the face of the statement.
function roundLargestRemainder(values) {
  const exact = values.reduce((s, v) => s + (v || 0), 0);
  const target = Math.round(exact);
  const rounded = values.map(v => Math.round(v || 0));
  let residual = target - rounded.reduce((s, r) => s + r, 0);
  if (residual === 0 || values.length === 0) return rounded;
  // Order indices by |value| desc so the largest line absorbs the residual.
  const order = values.map((_, i) => i).sort((a, b) => Math.abs(values[b]) - Math.abs(values[a]));
  const step = residual > 0 ? 1 : -1;
  for (let i = 0; i < order.length && residual !== 0; i++) {
    rounded[order[i]] += step;
    residual -= step;
  }
  return rounded;
}

// Variant of largest-remainder rounding that targets a CALLER-SUPPLIED
// total rather than round0(sum(values)). Used by the CF display footing
// reconciliation to force sum-of-displayed-rows = displayed_netChange =
// displayed_endCash − displayed_beginCash, so the CF face foots
// arithmetically (printed totals = sum of printed children) AND ties to
// the BS Cash & Equivalents printed total.
function roundLargestRemainderToTarget(values, target) {
  const rounded = values.map(v => Math.round(v || 0));
  let residual = target - rounded.reduce((s, r) => s + r, 0);
  if (residual === 0 || values.length === 0) return rounded;
  const order = values.map((_, i) => i).sort((a, b) => Math.abs(values[b]) - Math.abs(values[a]));
  const step = residual > 0 ? 1 : -1;
  for (let i = 0; i < order.length && residual !== 0; i++) {
    rounded[order[i]] += step;
    residual -= step;
  }
  return rounded;
}

// Sum a set of QBO account ids using the natural balance field
function sumIds(accounts, ids, field = 'balance') {
  return (ids || []).reduce((s, id) => {
    const a = accounts.find(acc => acc.id === id);
    return s + (a ? (a[field] || 0) : 0);
  }, 0);
}

// Compute the displayed value of a single mapped line, with the section's
// natural sign flipped in. Use `period='prior'` to compute against
// prior_balance instead of balance. Accepts both leaves ({accounts}) and
// groups ({type:'group', children:[leaf,...]}).
// Canonical cash rule — single source of truth, mirrors cashFlowV21
// isCashAccount and the BS cash IIFE in buildBalanceSheet.
export function isCanonicalCashAccount(a) {
  const t = (a?.qbo_type || '').toLowerCase();
  if (t === 'bank') return true;
  if (t === 'other current asset' && /undeposited/i.test(a?.name || '')) return true;
  return false;
}

// V23.5 — Fiscal-YTD Net Income anchor for the Balance Sheet equity section.
// QBO's BS "Net Income" row is fiscal-year-to-date through period_end. When
// the reporting period starts MID-fiscal-year (e.g. Q2: Apr 1), the IS shows
// PERIOD NI only; the BS must add the YTD NI already earned before the period
// started (qboMeta.ni_at_period_start) or Assets ≠ Liab + Equity by exactly
// that amount (LW Concierge Q2 2026: $22,368.37). When the period starts at
// fiscal-year start (Jan 1), ni_at_period_start is the PRIOR fiscal year's NI
// — already closed into Retained Earnings — and must NOT be added.
export function bsNetIncomeAnchor(periodNI, qboMeta) {
  const ps = qboMeta?.period_start;
  const niAtStart = Number(qboMeta?.ni_at_period_start);
  if (!ps || !Number.isFinite(niAtStart)) return periodNI;
  const isFyStart = /^\d{4}-01-01$/.test(String(ps));
  return isFyStart ? periodNI : periodNI + niAtStart;
}

// Mapped BS sections. Cash membership is COMPUTED (2026-06-03 spec), so a
// canonical-cash account redundantly present in any of these mapped
// sections must contribute $0 — otherwise the BS face double-counts it
// (once in the computed Cash & Equivalents group, once in the mapped
// line). LW Concierge bug (2026-08-19): 'Undeposited Funds' mapped in
// assets_current inflated Total Assets by exactly its balance.
const BS_MAPPED_SECTIONS = new Set([
  'assets_current', 'assets_fixed', 'assets_other',
  'liabilities_current', 'debt_short', 'liabilities_long', 'equity',
]);

export function lineValue(accounts, sectionKey, line, period = 'current') {
  const sign = SECTION_SIGN[sectionKey] ?? 1;
  const field = period === 'prior' ? 'prior_balance' : 'balance';
  const pool = BS_MAPPED_SECTIONS.has(sectionKey)
    ? (accounts || []).filter(a => !isCanonicalCashAccount(a))
    : accounts;
  if (line && line.type === 'group') {
    return (line.children || []).reduce(
      (s, child) => s + sign * sumIds(pool, child.accounts, field), 0
    );
  }
  return sign * sumIds(pool, line.accounts, field);
}

// Total a mapping section across all its mapped lines, with sign applied.
// Returns the unrounded float total — use this for tie/validation math.
// Display totals (sum of displayed rounded lines) are computed separately
// inside buildIncomeStatement / buildBalanceSheet.
//
// SPECIAL CASE — 'cash':
// As of 2026-06-03, the BS Cash & Equivalents section is no longer
// mapping-driven (see buildBalanceSheet inline comment for rationale).
// sectionTotal('cash', ...) must therefore reflect the same canonical
// per-account computation the BS uses, so the cf_ending_cash_ties_to_bs
// validation tie compares apples-to-apples. Otherwise CF.endCash (V21,
// canonical) would never equal sectionTotal('cash') (mapping-driven),
// and the tie would fail every time mapping.cash is empty or stale.
export function sectionTotal(accounts, mapping, sectionKey, period = 'current') {
  if (sectionKey === 'cash') {
    const undepositedRe = /undeposited/i;
    return (accounts || []).reduce((t, a) => {
      const ty = (a.qbo_type || '').toLowerCase();
      const isCash = ty === 'bank'
        || (ty === 'other current asset' && undepositedRe.test(a.name || ''));
      if (!isCash) return t;
      // period='prior' is the BS prior-year-same-date column (e.g. May 31 2025
      // for a 4/30/2026 period end). Map to the backend's `prior_balance`
      // snapshot — NOT `period_start_balance` (the current-period start,
      // e.g. Dec 31 2025, used by the CF beginCash).
      const v = period === 'prior'
        ? (Number(a.prior_balance) || 0)
        : (Number(a.balance) || 0);
      return t + v;
    }, 0);
  }
  if (!mapping || !mapping[sectionKey]) return 0;
  return mapping[sectionKey].reduce(
    (t, line) => t + lineValue(accounts, sectionKey, line, period), 0
  );
}

// Build the structured Income Statement: sections of normalized lines + key totals.
// Filters out lines that are $0 in both current and prior period unless
// `showZeroes` is true. Uses largest-remainder rounding so:
//   - Displayed section subtotal = sum of displayed lines (exact, no drift)
//   - Displayed grand totals computed from unrounded floats, rounded once
//   - Cross-statement Net Income matches QBO to the dollar (no double-rounding)
// V22 (cutover, 2026-02-28) — thin wrapper around `buildIncomeStatementV22`
// in ./incomeStatementV22.js. V20's IS builder (TB-based sectionTotal walk)
// has been deleted — it silently rendered YTD-from-FY-start for any
// non-FYTD period (the same architectural failure V21 fixed for the CF).
//
// V22 reads period-scoped values directly from QBO's ProfitAndLoss report.
// Public signature is preserved so renderers, KPIs, validation ties, and
// PrintModal all consume the same {sections, totals, prior} shape they
// did under V20.
//
// `opts.plPeriod` and `opts.plPeriodPrior` are the V22 inputs — when
// present, the builder uses QBO's per-period numbers directly. When
// absent (consolidated rollup paths, fixtures without QBO connection),
// the wrapper synthesizes a minimal plPeriod from accounts' TB
// balances; this is V22 consuming synthesized inputs, NOT a separate
// V20 implementation, and the synthesized fallback is documented as
// legacy mode (same numbers V20 used to produce — i.e., FYTD when the
// caller can't provide period-scoped P&L).
//
// See /app/memory/V22_DAY2_DIVERGENCE_LOG.md for the 27/27 cross-statement
// cardinal-tie acceptance matrix.
import { buildIncomeStatementV22 } from './incomeStatementV22.js';

export function buildIncomeStatement(accounts, mapping, opts = {}) {
  const showZeroes = opts.showZeroes === true;

  // Synthesize a plPeriod from per-account TB balances when the caller
  // doesn't have one (consolidated path, fixtures, demo). This produces
  // the same FYTD-from-FY-start numbers V20 used to surface — V22 then
  // renders them through its own pipeline.
  const synthesize = (which /* 'current' | 'prior' */) => {
    const bal = (a) => which === 'prior'
      ? (Number(a.prior_balance) || 0)
      : (Number(a.balance) || 0);
    const section_totals = {
      Income: 0, COGS: 0, Expenses: 0, OtherIncome: 0, OtherExpenses: 0,
    };
    const detail_lines = [];
    for (const a of (accounts || [])) {
      const cls = String(a.classification || '').toLowerCase();
      const qbo_type = String(a.qbo_type || '').toLowerCase();
      const raw = bal(a);
      // Match V20 sign convention: income/other_income are credit-natural
      // (negative in TB), expenses positive. Present as POSITIVE values in
      // QBO P&L conventional shape (Income section sums to positive revenue).
      let group = null, presentation = 0;
      if (qbo_type === 'income' && !/other/i.test(qbo_type)) {
        group = 'Income'; presentation = -raw;
      } else if (qbo_type === 'other income') {
        group = 'OtherIncome'; presentation = -raw;
      } else if (qbo_type === 'cost of goods sold' || /cogs/.test(qbo_type)) {
        group = 'COGS'; presentation = raw;
      } else if (qbo_type === 'other expense') {
        group = 'OtherExpenses'; presentation = raw;
      } else if (qbo_type === 'expense' || cls === 'expense') {
        group = 'Expenses'; presentation = raw;
      } else if (cls === 'revenue') {
        group = 'Income'; presentation = -raw;
      }
      if (!group) continue;
      section_totals[group] += presentation;
      detail_lines.push({
        section: group,
        name: a.name,
        qbo_account_id: String(a.id),
        value: presentation,
      });
    }
    const netIncome = section_totals.Income
      - section_totals.COGS
      - section_totals.Expenses
      + section_totals.OtherIncome
      - section_totals.OtherExpenses;
    return {
      section_totals,
      detail_lines,
      net_income: netIncome,
      depreciation: 0, amortization: 0,
      interest_expense: 0, tax_expense: 0,
      _synthesized: true,
    };
  };

  const plPeriod      = opts.plPeriod      || synthesize('current');
  const plPeriodPrior = opts.plPeriodPrior || synthesize('prior');

  const v22 = buildIncomeStatementV22(accounts, mapping, plPeriod, plPeriodPrior);

  // Translate V22's output (which already matches V20's shape) and
  // enrich with the legacy line filtering V20 applied via showZeroes.
  const filterSection = (rawLines) => {
    if (showZeroes) return rawLines;
    const filt = [];
    const walk = (l) => {
      if (l.type === 'group') {
        const kept = (l.children || []).filter(c =>
          Math.abs(c.current || 0) > 0 || Math.abs(c.prior || 0) > 0);
        if (kept.length === 0) return;
        filt.push({ ...l, children: kept });
      } else {
        if (Math.abs(l.current || 0) > 0 || Math.abs(l.prior || 0) > 0) {
          filt.push(l);
        }
      }
    };
    for (const ln of rawLines) walk(ln);
    return filt;
  };

  // V20-shape `sections.*.{lines, items, total, total_prior, exact, exact_prior}`
  // for renderer compatibility.
  const wrapSection = (rawLines, qboKey) => {
    const visibleLines = filterSection(rawLines);
    const items = visibleLines.map(l => {
      if (l.type === 'group') {
        return {
          kind: 'group', label: l.label,
          leaves: (l.children || []).map(c => ({
            label: c.label, current: c.current, prior: c.prior,
            accounts: c.accounts || [],
          })),
          subtotal:       l.current,
          subtotal_prior: l.prior,
        };
      }
      return {
        kind: 'leaf', label: l.label,
        current: l.current, prior: l.prior,
        accounts: l.accounts || [],
        residual: l.kind === 'unmapped-residual',
      };
    });
    // Flat `lines` shape (legacy): one row per leaf.
    const flat = [];
    const flatten = (l) => {
      if (l.type === 'group') (l.children || []).forEach(flatten);
      else flat.push({
        label: l.label, current: l.current, prior: l.prior,
        accounts: l.accounts || [],
        _exactCurrent: l.current, _exactPrior: l.prior,
      });
    };
    for (const ln of rawLines) flatten(ln);

    const exact      = Number(v22.totals[ {
      income: 'totalRevenue', cogs: 'totalCOGS', opex: 'totalOpex',
      other_income: 'totalOtherIncome', other_expense: 'totalOtherExpense',
    }[qboKey] ]) || 0;
    const exact_prior = Number(v22.prior[ {
      income: 'totalRevenue', cogs: 'totalCOGS', opex: 'totalOpex',
      other_income: 'totalOtherIncome', other_expense: 'totalOtherExpense',
    }[qboKey] ]) || 0;

    return {
      key: qboKey,
      lines: showZeroes ? flat : flat.filter(r => Math.abs(r.current) > 0 || Math.abs(r.prior) > 0),
      items,
      total:       round0(exact),
      total_prior: round0(exact_prior),
      exact,
      exact_prior,
    };
  };

  const income       = wrapSection(v22.sections.revenue,      'income');
  const cogs         = wrapSection(v22.sections.cogs,         'cogs');
  const opex         = wrapSection(v22.sections.opex,         'opex');
  const otherIncome  = wrapSection(v22.sections.otherIncome,  'other_income');
  const otherExpense = wrapSection(v22.sections.otherExpense, 'other_expense');

  return {
    builder: 'v22',
    sections: { income, cogs, opex, otherIncome, otherExpense },
    totals: {
      totalRevenue:  round0(v22.totals.totalRevenue),
      totalCOGS:     round0(v22.totals.totalCOGS),
      grossProfit:   round0(v22.totals.grossProfit),
      totalOpEx:     round0(v22.totals.totalOpex),
      ebit:          round0(v22.totals.incomeFromOps),
      otherIncome:   round0(v22.totals.totalOtherIncome),
      otherExpense:  round0(v22.totals.totalOtherExpense),
      netIncome:     round0(v22.totals.netIncome),
    },
    prior: {
      totalRevenue:  round0(v22.prior.totalRevenue),
      totalCOGS:     round0(v22.prior.totalCOGS),
      grossProfit:   round0(v22.prior.grossProfit),
      totalOpEx:     round0(v22.prior.totalOpex),
      ebit:          round0(v22.prior.incomeFromOps),
      otherIncome:   round0(v22.prior.totalOtherIncome),
      otherExpense:  round0(v22.prior.totalOtherExpense),
      netIncome:     round0(v22.prior.netIncome),
    },
    _v22: {
      plPeriodSynthesized:      !!plPeriod._synthesized,
      plPeriodPriorSynthesized: !!plPeriodPrior._synthesized,
      qboNetIncome:             Number(plPeriod?.net_income) || 0,
      qboNetIncomePrior:        Number(plPeriodPrior?.net_income) || 0,
      // V18.1 — expose unrounded V22 totals for validation ties that
      // must compare floats (e.g. t12_ni_ties_to_is_ytd compares
      // unrounded NI from each monthly V22 run vs unrounded YTD NI).
      exactNetIncome:           Number(v22.totals.netIncome) || 0,
      exactNetIncomePrior:      Number(v22.prior.netIncome) || 0,
    },
    // V18 — forward V22 diagnostic block so validation ties (is_no_section_residual,
    // ni_displayed_consistent) can consume orphan accounts + section residuals
    // from the wrapper output without re-running buildIncomeStatementV22.
    diagnostic: v22.diagnostic,
  };
}

// Build the structured Balance Sheet.
// Uses largest-remainder rounding so displayed lines tie to subtotals, and
// applies a final tie-up pass on the largest equity line so Total Assets and
// Total Liabilities + Equity foot to the dollar on the face of the statement.
//
// Each section returns BOTH:
//   - lines: flat ordered list of leaves (legacy shape, kept for math callers)
//   - items: structured render order — { kind: 'group' | 'leaf', ... }
//            with group leaves indented one extra level and a group subtotal
//            rendered after each group. This is the canonical shape every
//            renderer (BS, BS PY, IS, IS PY, T12, CF) consumes.
//
// Group shape in mapping:
//   { type: 'group', label, children: [ { label, accounts: [ids] }, ... ] }
// Leaf shape (unchanged):
//   { label, accounts: [ids] }
//
// Legacy bridge: the existing `mapping.cash` section is auto-promoted as a
// group at the head of `assetsCur.items` so the BS renders Cash & Equivalents
// as a structural group inside Current Assets without requiring a mapping
// migration. `mapping.cash` continues to exist for backwards-compat readers
// (math callers that haven't moved to `items` yet) but the items pipeline is
// the single source of truth for rendering.
export function buildBalanceSheet(accounts, mapping, niExact = null, niPriorExact = null, { showZeroes = false } = {}) {
  // Backwards-compat: if a caller passed an `options` object in the
  // niPriorExact slot (the legacy 4-arg signature), normalize.
  if (niPriorExact !== null && typeof niPriorExact === 'object') {
    showZeroes = niPriorExact.showZeroes ?? showZeroes;
    niPriorExact = null;
  }
  // Build a section into both a flat lines array (legacy) and a structured
  // items array (group + leaf). Empty groups are still emitted when
  // showZeroes is true so the editor experience is consistent.
  const buildSection = (key, opts = {}) => {
    const rawItems = (mapping?.[key] || []);
    // Flatten all leaves for math + the legacy `lines` shape.
    const allLeaves = []; // [{label, accounts, fromGroup, groupIdx, leafIdx}]
    rawItems.forEach((entry, gi) => {
      if (entry && entry.type === 'group') {
        (entry.children || []).forEach((child, li) => {
          allLeaves.push({ label: child.label, accounts: child.accounts || [],
                           fromGroup: gi, groupLabel: entry.label, leafIdx: li });
        });
      } else {
        allLeaves.push({ label: entry.label, accounts: entry.accounts || [],
                         fromGroup: null, leafIdx: gi });
      }
    });

    const exactCurrent = allLeaves.map(l => lineValue(accounts, key, l, 'current'));
    const exactPrior   = allLeaves.map(l => lineValue(accounts, key, l, 'prior'));
    const dispCurrent  = roundLargestRemainder(exactCurrent);
    const dispPrior    = roundLargestRemainder(exactPrior);

    // Build the legacy flat lines view
    const flatLines = allLeaves.map((leaf, i) => ({
      idx: i,
      label: leaf.label,
      accounts: leaf.accounts,
      current: dispCurrent[i],
      prior:   dispPrior[i],
      _exactCurrent: exactCurrent[i],
      _exactPrior:   exactPrior[i],
    }));
    const visibleFlat = (showZeroes || opts.alwaysShow)
      ? flatLines
      : flatLines.filter(r => Math.abs(r.current) > 0 || Math.abs(r.prior) > 0);

    // Build the structured items view. We walk rawItems in original order.
    const items = [];
    let leafCursor = 0;
    rawItems.forEach((entry) => {
      if (entry && entry.type === 'group') {
        const groupLeaves = (entry.children || []).map((_, li) => {
          const leaf = flatLines[leafCursor + li];
          return leaf;
        });
        leafCursor += (entry.children || []).length;
        const visible = (showZeroes || opts.alwaysShow)
          ? groupLeaves
          : groupLeaves.filter(r => r && (Math.abs(r.current) > 0 || Math.abs(r.prior) > 0));
        // Hide empty groups entirely unless showZeroes
        if (visible.length === 0 && !showZeroes) return;
        const subtotal       = groupLeaves.reduce((s, l) => s + (l?.current || 0), 0);
        const subtotal_prior = groupLeaves.reduce((s, l) => s + (l?.prior   || 0), 0);
        items.push({
          kind: 'group',
          label: entry.label,
          leaves: visible,
          subtotal,
          subtotal_prior,
        });
      } else {
        const leaf = flatLines[leafCursor];
        leafCursor += 1;
        if (!leaf) return;
        if (!showZeroes && !opts.alwaysShow &&
            Math.abs(leaf.current) === 0 && Math.abs(leaf.prior) === 0) return;
        items.push({ kind: 'leaf', label: leaf.label, current: leaf.current,
                     prior: leaf.prior, accounts: leaf.accounts });
      }
    });

    return {
      key,
      lines: visibleFlat,
      items,
      total:       dispCurrent.reduce((s, n) => s + n, 0),
      total_prior: dispPrior.reduce((s, n) => s + n, 0),
      exact:       exactCurrent.reduce((s, n) => s + n, 0),
      exact_prior: exactPrior.reduce((s, n) => s + n, 0),
    };
  };

  const cash         = (() => {
    // ─── ARCHITECTURAL: Cash section is COMPUTED, not MAPPED ──────────
    //
    // Spec (2026-06-03): "Mapping may set a line's label and section
    // ONLY; it must never determine whether a changed account appears."
    //
    // The BS Cash & Equivalents group is the source of truth for both:
    //   (a) the BS section subtotal displayed at the top of Current
    //       Assets, and
    //   (b) the CF endCash anchor that the cf_ending_cash_ties_to_bs
    //       validation tie checks against (via sectionTotal('cash')
    //       below).
    //
    // It MUST be derived directly from per-account QBO classification —
    // identical to the rule the V21 CF builder uses for isCashAccount —
    // not from `mapping.cash`. Otherwise the BS Cash line and the CF
    // endCash diverge whenever a bank/undeposited account is on QBO
    // but absent from the (optional, advisory) mapping.cash array.
    //
    // The Las Trojas Fayetteville case (2026-06-03): "Undeposited
    // Funds" carried a $5,719.85 period-end balance. V21 CF correctly
    // counted it as cash. BS (which used mapping.cash, which was
    // empty for that engagement) showed Cash & Equivalents =
    // -$53,744.59 from the single overdrafted bank account. Variance
    // $5,720, ending-cash tie failed.
    //
    // Cash set definition (matches V21 cashFlowV21.js isCashAccount):
    //   • qbo_type == 'Bank'  → ALWAYS cash
    //   • qbo_type == 'Other Current Asset' AND name matches
    //     /undeposited/i → cash (QBO's "Undeposited Funds")
    //   • everything else → not cash
    //
    // Sub-account safety: leaf-only — QBO accounts payload is already
    // flat leaves with their own balances. No parent rollup risk.
    const undepositedRe = /undeposited/i;
    const cashAccounts = (accounts || []).filter(a => {
      const t = (a.qbo_type || '').toLowerCase();
      if (t === 'bank') return true;
      if (t === 'other current asset' && undepositedRe.test(a.name || '')) return true;
      return false;
    });
    // Sort by descending |current balance| for CPA-conventional display.
    cashAccounts.sort((x, y) =>
      Math.abs(Number(y.balance) || 0) - Math.abs(Number(x.balance) || 0));

    const exactCurrent = cashAccounts.map(a => Number(a.balance) || 0);
    // BS prior-year-same-date column. MUST source from `prior_balance`
    // (May 31 2025 snapshot for a 4/30/2026 period end). `period_start_balance`
    // is a different anchor (current period start, e.g. Dec 31 2025 / FY start)
    // used by the CF builder for beginCash — using it here would silently
    // emit the period-start cash balance in the prior-year column.
    const exactPrior   = cashAccounts.map(a => Number(a.prior_balance) || 0);
    const dispCurrent  = roundLargestRemainder(exactCurrent);
    const dispPrior    = roundLargestRemainder(exactPrior);

    // One displayed line per cash account, using the account's QBO name.
    // Future: if user wants to override the label, that's the ONE thing
    // mapping.cash could legitimately do — set per-account label
    // overrides — without changing membership.
    const flatLines = cashAccounts.map((a, i) => ({
      idx: i,
      label: a.name,
      accounts: [a.id],
      current: dispCurrent[i],
      prior:   dispPrior[i],
      _exactCurrent: exactCurrent[i],
      _exactPrior:   exactPrior[i],
    }));
    const visibleFlat = showZeroes
      ? flatLines
      : flatLines.filter(r => Math.abs(r.current) > 0 || Math.abs(r.prior) > 0);

    return {
      key: 'cash',
      lines: visibleFlat,
      items: visibleFlat.map(l => ({
        kind: 'leaf', label: l.label, current: l.current, prior: l.prior,
        accounts: l.accounts,
      })),
      total:       dispCurrent.reduce((s, n) => s + n, 0),
      total_prior: dispPrior.reduce((s, n) => s + n, 0),
      exact:       exactCurrent.reduce((s, n) => s + n, 0),
      exact_prior: exactPrior.reduce((s, n) => s + n, 0),
      _canonical:  true,  // marks this as architecturally-correct, not mapping-derived
    };
  })();
  const assetsCur    = buildSection('assets_current');
  const assetsFix    = buildSection('assets_fixed');
  const assetsOther  = buildSection('assets_other');
  const liabCur      = buildSection('liabilities_current');
  const debtShort    = buildSection('debt_short');
  const liabLong     = buildSection('liabilities_long');
  const equity       = buildSection('equity');

  // Legacy-bridge: promote `cash` section into a synthetic Cash & Equivalents
  // group at the top of Current Assets in the structured `items` view. Math
  // totals already include cash separately, so we only do this for rendering.
  if (cash.lines.length > 0) {
    assetsCur.items = [
      {
        kind: 'group',
        label: 'Cash & Equivalents',
        leaves: cash.lines.map(l => ({
          label: l.label, current: l.current, prior: l.prior, accounts: l.accounts,
        })),
        subtotal:       cash.total,
        subtotal_prior: cash.total_prior,
      },
      ...assetsCur.items,
    ];
  }
  // Combined Current Assets total = cash + non-cash CA. Renderers use this
  // for the section total so it ties to the displayed group + leaves.
  assetsCur.totalCombined       = cash.total       + assetsCur.total;
  assetsCur.totalCombined_prior = cash.total_prior + assetsCur.total_prior;
  assetsCur.exactCombined       = cash.exact       + assetsCur.exact;
  assetsCur.exactCombined_prior = cash.exact_prior + assetsCur.exact_prior;

  // Grand totals from UNROUNDED floats — referenced for validation ties
  // and ROUND_ADJUST_CAP debugging only. Displayed totals below are
  // sum-of-displayed-components (see DISPLAY FOOTING INTEGRITY).
  const exactAssets = cash.exact + assetsCur.exact + assetsFix.exact + assetsOther.exact;
  const exactLiab   = liabCur.exact + debtShort.exact + liabLong.exact;
  const exactAssetsPrior = cash.exact_prior + assetsCur.exact_prior + assetsFix.exact_prior + assetsOther.exact_prior;
  const exactLiabPrior   = liabCur.exact_prior + debtShort.exact_prior + liabLong.exact_prior;

  // ─── DISPLAY FOOTING INTEGRITY ─────────────────────────────────────
  //
  // Every printed total = sum of its printed (rounded) children. A
  // reader hand-adding the printed numbers must reproduce every total.
  // This is non-negotiable on the BS face; the validation ties' $1
  // tolerance applies to internal cent-level reconciliation only.
  //
  // - Section subtotals come from `*.total` (already sum-of-displayed-lines
  //   via largest-remainder in buildSection / cash IIFE).
  // - Grand totals are computed as SUM of section subtotals — not as
  //   round0(exact_sum), which can differ by sub-cent fractional drift.
  const totalAssets      = cash.total       + assetsCur.total       + assetsFix.total       + assetsOther.total;
  const totalAssetsPrior = cash.total_prior + assetsCur.total_prior + assetsFix.total_prior + assetsOther.total_prior;
  const totalLiab        = liabCur.total       + debtShort.total       + liabLong.total;
  const totalLiabPrior   = liabCur.total_prior + debtShort.total_prior + liabLong.total_prior;

  // Equity bounded redistribution — sub-cent rounding only (cap $2).
  //
  // Without this, sum(equity.lines) + NI may differ from totalAssets -
  // totalLiab by $1-2 due to largest-remainder drift across independent
  // section rounding. The cap of $2 ensures sub-cent rounding closes
  // the BS face without masking real data errors (the $152K Fayetteville
  // plug, for example, overflows the cap and surfaces via the
  // `bs_py_matches_prior_year_snapshot` validation tie).
  //
  // Previously this block defined totalEquity = totalAssets - totalLiab
  // and applied an UNBOUNDED mutation to equity.lines, which silently
  // absorbed any data error into Partners' Equity.
  const niExactCurrent = niExact !== null ? niExact : 0;
  const niExactPrior   = niPriorExact !== null ? niPriorExact : 0;
  const niRoundedCurrent = round0(niExactCurrent);
  const niRoundedPrior   = round0(niExactPrior);
  const ROUND_ADJUST_CAP = 2;
  const equityRound = (lineKey, niRounded, totalAssetsRounded, totalLiabRounded) => {
    if (equity.lines.length === 0) return;
    const target = totalAssetsRounded - totalLiabRounded - niRounded;
    const currentSum = equity.lines.reduce((s, l) => s + (l[lineKey] || 0), 0);
    const adjust = target - currentSum;
    if (adjust === 0) return;
    if (Math.abs(adjust) > ROUND_ADJUST_CAP) {
      // Real data error — leave equity at the raw snapshot value; the
      // validation ties will fire and block publish.
      return;
    }
    const sorted = [...equity.lines].sort((a, b) =>
      Math.abs(b[lineKey] || 0) - Math.abs(a[lineKey] || 0));
    if (sorted[0]) sorted[0][lineKey] += adjust;
  };
  equityRound('current', niRoundedCurrent, totalAssets,      totalLiab);
  equityRound('prior',   niRoundedPrior,   totalAssetsPrior, totalLiabPrior);
  equity.total       = equity.lines.reduce((s, l) => s + (l.current || 0), 0);
  equity.total_prior = equity.lines.reduce((s, l) => s + (l.prior   || 0), 0);
  // Mirror the line-level adjustment into equity.items so renderers
  // that walk items (instead of lines) stay consistent.
  if (equity.items.length > 0) {
    const reflect = (lineKey, itemKey) => {
      const sorted = [...equity.lines].sort((a, b) =>
        Math.abs(b[lineKey] || 0) - Math.abs(a[lineKey] || 0));
      const largestLabel = sorted[0]?.label;
      if (!largestLabel) return;
      const idx = equity.items.findIndex(it => it.kind === 'leaf' && it.label === largestLabel);
      if (idx < 0) return;
      const ln = equity.lines.find(l => l.label === largestLabel);
      if (ln) equity.items[idx][itemKey] = ln[lineKey];
    };
    reflect('current', 'current');
    reflect('prior',   'prior');
  }
  // Equity displayed totals = sum-of-displayed equity lines + NI
  // (matches the BS face composition exactly).
  const totalEquity      = equity.total       + niRoundedCurrent;
  const totalEquityPrior = equity.total_prior + niRoundedPrior;

  return {
    sections: {
      cash, assetsCur, assetsFix, assetsOther,
      liabCur, debtShort, liabLong, equity,
    },
    totals: {
      totalAssets, totalLiab, totalEquity,
      totalEquityBeforeNI: equity.total,
      totalLE: totalLiab + totalEquity,
      // NI displayed alongside the equity section on the BS face
      netIncome: round0(niExactCurrent),
    },
    prior: {
      totalAssets:      totalAssetsPrior,
      totalLiab:        totalLiabPrior,
      totalEquity:      totalEquityPrior,
      totalEquityBeforeNI: equity.total_prior,
      totalLE:          totalLiabPrior + totalEquityPrior,
      netIncome:        round0(niExactPrior),
    },
    exact: {
      totalAssets: exactAssets,
      totalLiab:   exactLiab,
      totalEquityBeforeNI: equity.exact,
      totalAssetsPrior: exactAssetsPrior,
      totalLiabPrior:   exactLiabPrior,
      totalEquityPriorBeforeNI: equity.exact_prior,
    },
  };
}

// Build the canonical Statement of Cash Flows (indirect method).
//
// V15.1 (2026-05-23 hotfix) — addresses peer-review of V15 CF:
//   • V15 differenced against `prior_balance` (12-month-prior TB), producing
//     a mathematically incoherent statement that mixed 4-month YTD NI with
//     12-month BS deltas. The financing line absorbed the inconsistency as
//     a plug; mechanical ties passed; ECONOMIC representation was wrong.
//   • V15.1 differences against `period_start_balance` (BS snapshot at the
//     eve of the reporting period), supplied by the backend QBO ingestion.
//   • D&A add-back now sourced from the IS (YTD depreciation/amortization
//     expense), not from BS contra-asset deltas (which spanned the wrong
//     period under V15).
//   • CF rows now aggregate at the CANONICAL MAPPING LINE level, not raw
//     QBO leaves. Credit Card Payable shows as a single line with NET
//     delta — small below-threshold lines roll up as before.
//
// Carve-outs (V15 preserved):
//   • Depreciation & Amortization — own line(s), never thresholded.
//   • Supplemental disclosures (cash paid for interest, cash paid for
//     taxes) — own lines under ASC 230, never thresholded.
//
// Signature:
//   buildCashFlow(accounts, mapping, niExact, niPriorExact, opts)
//   opts: {
//     materialityThreshold: number|null,   // explicit; null = auto
//     totalAssets: number|null,            // for auto-threshold formula
//     niAtPeriodStart: number|null,        // V15.1: synthetic NI on the
//                                          //        BS at period_start_eve;
//                                          //        subtracted from primary
//                                          //        equity delta to back
//                                          //        out the prior YTD NI
//                                          //        already absorbed there.
//     dAndAExpense: number|null,           // V15.1: YTD D&A expense from
//                                          //        the IS (preferred);
//                                          //        when null, auto-derive
//                                          //        from mapping labels.
//     interestExpense: number|null,
//     taxExpense: number|null,
//   }
//
// Returns:
//   { hasPeriodStart, netIncome, dAndA, threshold, thresholdAutoComputed,
//     sections: { operating, investing, financing }
//                  each: { rows:[{label, value, indent, subheader?, rollup?}],
//                          subtotal },
//     supplemental: { interestPaid, taxesPaid },
//     netChange, beginCash, endCash,
//     bsLinesRepresented, nonCashBSDeltaTotal,
//     // Legacy alias for renderers that still check `hasPrior`:
//     hasPrior }
// V21 (Day 3 cutover, 2026-02-28) — thin wrapper around `buildCashFlowV21`
// in ./cashFlowV21.js. The 743-line V20 derivation pipeline has been
// deleted in favor of a single-path read from QBO:
//   - Net Income from QBO ProfitAndLoss (period-scoped, honors arbitrary
//     start_date/end_date — see /app/memory/V21_DAY1_GATE_RESULTS.md).
//   - D&A add-back from the BS Accumulated Depreciation delta — the
//     accounting ground truth (per Architectural Review §4, Option 2).
//   - Universal sign rule for non-cash accounts: `value = -raw_delta`.
//   - Single combined equity line with BS NetIncome row movement
//     incorporated (handles all periods: FY-aligned, mid-month, Q4
//     prior-year, cross-FY, single day — see V21_DAY2_DIVERGENCE_LOG.md
//     for the 24/24 acceptance matrix at exact $0.00 identity).
//
// Deleted V20 derivation branches: B2/B3/B4 (niAtPS equity back-out),
// B5/B6 (D&A regex-from-IS), B9 (AccumDep contra-exclusion in Investing),
// B10 (Path B entity-supplement walk), the niAtPS materiality threshold,
// reconciliation advisory, and the niAtPS-drift-within-materiality
// validation tie. The architecture no longer requires any of them.
//
// Public signature preserved so renderers and validation ties consume
// the same object shape they did under V20. `opts.plPeriod` is the new
// V21 input — when present, it carries QBO's period-scoped P&L (NI,
// depreciation, amortization, interest, tax). When absent (legacy
// callers, consolidation merge), the wrapper synthesizes a minimal
// plPeriod from niExact and the BS-derived AccumDep delta and runs V21.
//
// See:
//   /app/memory/CF_BUILDER_ARCHITECTURAL_REVIEW.md  — the case for V21
//   /app/memory/V21_DAY1_GATE_RESULTS.md            — QBO P&L proof
//   /app/memory/V21_DAY2_DIVERGENCE_LOG.md          — full V20→V21 diff
//   /app/CONTRIBUTING.md                            — "builders read, do
//                                                     not reconstruct"
import { buildCashFlowV21 } from './cashFlowV21.js';

export function buildCashFlow(accounts, mapping, niExact, niPriorExact = 0, opts = {}) {
  const hasPeriodStart = accounts.some(a => Math.abs(a.period_start_balance || 0) > 0.005);
  const hasPrior = hasPeriodStart || accounts.some(a => Math.abs(a.prior_balance || 0) > 0.005);

  // Build the V21 plPeriod input. Preferred path: caller passes
  // `opts.plPeriod` straight from qboMeta.pl_period (added by the V21
  // backend cutover — see /api/qbo/accounts/{cid} response). Fallback:
  // synthesize the minimum {net_income, depreciation, amortization}
  // shape from niExact + an AccumDep BS-delta scan (Option 2 D&A).
  let plPeriod = opts.plPeriod;
  if (!plPeriod) {
    // Synthesize. Sum AccumDep raw deltas (these are credit-natural
    // contra-assets — raw goes more negative as AccumDep grows; D&A
    // presentation value is the absolute increase).
    const isAccumContraName = (n) => {
      const s = String(n || '').toLowerCase();
      return /accumulated\s+(depreciat|amortiz)/.test(s)
          || /\baccum(\.|ulated)?\s+(depr|amort)/.test(s)
          || /\bless\s+accum/.test(s);
    };
    let accumRaw = 0;
    for (const a of accounts) {
      if (isAccumContraName(a.name)) {
        accumRaw += (Number(a.balance) || 0) - (Number(a.period_start_balance) || 0);
      }
    }
    plPeriod = {
      net_income: Number(niExact) || 0,
      depreciation: -accumRaw,  // Option 2 D&A
      amortization: 0,           // already rolled into the AccumDep regex above
      interest_expense: 0,
      tax_expense: 0,
    };
  }

  // V21 needs niAtPeriodEnd in the FY-YTD-through-period-end sense.
  // For FY-aligned callers, niExact IS this value. For cross-FY callers,
  // the caller should pass `opts.niAtPeriodEnd` explicitly (qboMeta-derived).
  const v21 = buildCashFlowV21(accounts, plPeriod, {
    niAtPeriodStart: opts.niAtPeriodStart != null
      ? Number(opts.niAtPeriodStart) : Number(niPriorExact) || 0,
    niAtPeriodEnd: opts.niAtPeriodEnd != null
      ? Number(opts.niAtPeriodEnd) : (Number(niExact) || 0),
    materialityThreshold: opts.materialityThreshold,
  });

  // ─── V21.1 canonical-leaf aggregation pass ──────────────────────────
  //
  // V21's builder emits one CF row PER QBO ACCOUNT. The canonical
  // mapping defines how the firm has grouped those accounts into
  // presentation LEAVES on the BS (e.g. the leaf "Accounts Payable" may
  // contain three QBO accounts: Accounts Payable + Employee Tips
  // Payable + Sales Tax Payable). For CPA presentation AND for the
  // validation tie `cf_period_matches_is_period` (WC sample check) to
  // pass, each canonical leaf needs to surface as ONE CF row whose
  // value sums all of its accounts' V21 values.
  //
  // This is a presentation aggregation. The math is unchanged — V21's
  // section subtotals are preserved exactly because aggregation just
  // collapses sibling rows. Identity still holds at $0.00 because we
  // sum the same values, just under fewer labels.
  //
  // Accounts not in any canonical leaf keep their own-name row (covers
  // empty-mapping fixtures and any account dropped from the canonical
  // structure).
  const accountIdToLeaf = new Map();  // account_id → { label, sectionKey }
  const bsSectionsForMap = ['assets_current', 'assets_fixed', 'assets_other',
                            'liabilities_current', 'debt_short',
                            'liabilities_long', 'equity'];
  const indexLeaf = (sectionKey, line) => {
    if (!line) return;
    if (line.type === 'group') {
      for (const c of (line.children || [])) indexLeaf(sectionKey, c);
      return;
    }
    for (const aid of (line.accounts || [])) {
      accountIdToLeaf.set(String(aid), { label: line.label, sectionKey });
    }
  };
  for (const sk of bsSectionsForMap) {
    for (const item of (mapping?.[sk] || [])) indexLeaf(sk, item);
  }

  const aggregateBySectionGroup = (rowsArr, getVerbLabel) => {
    // Net Income + D&A and any other rows without account_id pass through
    // unchanged (preserves the operating section's NI + D&A header rows).
    const aggregated = [];
    const byLeaf = new Map();  // leafLabel → { value, label, kind }
    for (const r of rowsArr) {
      if (!r.account_id) { aggregated.push(r); continue; }
      const leaf = accountIdToLeaf.get(String(r.account_id));
      // Account not in any canonical leaf → keep its own row.
      if (!leaf) { aggregated.push(r); continue; }
      const key = leaf.label;
      const existing = byLeaf.get(key);
      if (existing) {
        existing.value += r.value;
      } else {
        byLeaf.set(key, {
          label: getVerbLabel(leaf.label),
          value: r.value,
          kind: r.kind,
          canonical_label: leaf.label,
        });
      }
    }
    for (const agg of byLeaf.values()) aggregated.push(agg);
    // Re-sort presentation order by |value| desc for consistency.
    return aggregated.sort((a, b) => Math.abs(b.value || 0) - Math.abs(a.value || 0));
  };

  // Working-capital rows: use a single canonical verbose form.
  // (Assets read "(Increase) Decrease in X"; liabilities read "Increase
  // (Decrease) in X". Without per-account context after aggregation we
  // default to the liability form for liab leaves and the asset form for
  // asset leaves — drive off the leaf's sectionKey.)
  const wcLabelFor = (leafLabel) => {
    const leafEntry = [...accountIdToLeaf.values()].find(v => v.label === leafLabel);
    const sk = leafEntry?.sectionKey || '';
    if (sk.startsWith('assets_')) return `(Increase) Decrease in ${leafLabel}`;
    return `Increase (Decrease) in ${leafLabel}`;
  };
  v21.sections.operating.rows = aggregateBySectionGroup(
    v21.sections.operating.rows, wcLabelFor,
  );
  v21.sections.investing.rows = aggregateBySectionGroup(
    v21.sections.investing.rows, (l) => `Acquisitions (Disposals) of ${l}`,
  );
  v21.sections.financing.rows = aggregateBySectionGroup(
    v21.sections.financing.rows, (l) => `Proceeds from (Payments on) ${l}`,
  );
  // Subtotals recompute (aggregation is value-preserving but rebuild to
  // be defensive against any per-row rounding).
  v21.sections.operating.subtotal = v21.sections.operating.rows
    .reduce((s, r) => s + (r.value || 0), 0);
  v21.sections.investing.subtotal = v21.sections.investing.rows
    .reduce((s, r) => s + (r.value || 0), 0);
  v21.sections.financing.subtotal = v21.sections.financing.rows
    .reduce((s, r) => s + (r.value || 0), 0);
  v21.sectionsNet =
      v21.sections.operating.subtotal
    + v21.sections.investing.subtotal
    + v21.sections.financing.subtotal;
  v21.identityDelta = v21.sectionsNet - v21.netChange;
  v21.identityPasses = Math.abs(v21.identityDelta) < 1.00;

  // Auto-threshold (V15 formula carried forward — used purely for
  // presentation; V21's section walk applies it identically).
  const totalAssets = Number(opts.totalAssets) || 0;
  const thresholdExplicit = (opts.materialityThreshold != null
                          && Number.isFinite(opts.materialityThreshold)
                          && opts.materialityThreshold >= 0);
  const thresholdAuto = Math.max(1000, totalAssets * 0.005);
  const threshold = thresholdExplicit ? Number(opts.materialityThreshold) : thresholdAuto;
  const thresholdAutoComputed = !thresholdExplicit;

  // V20-compatible `bsLinesRepresented` — used by the
  // `cf_lines_reconcile_to_bs` validation tie. With V21, every non-cash
  // account contributes exactly one CF row (or is captured by the D&A
  // add-back via AccumDep, or aggregated into "Other ... activities", or
  // surfaced via `unclassified`). The math identity passing at $0.00
  // guarantees structural coverage.
  //
  // The coverage validation tie matches by CANONICAL MAPPING LEAF LABEL,
  // not by V21's CPA-presentation label ("(Increase) Decrease in X"). So
  // we walk the canonical mapping the same way the tie does, and for
  // each leaf with a meaningful delta we emit an entry under the
  // canonical leaf label. This is label translation, not a plug — V21's
  // math is unchanged; we are simply registering V21's coverage in the
  // canonical naming scheme so the tie's by-name lookup succeeds.
  //
  // V21's covered set = every non-cash account_id in `accounts`. (Cash
  // accounts are not in any canonical BS section either — the tie's
  // walker enumerates only assets_current/fixed/other, liabilities_*,
  // and equity, so cash is naturally excluded on both sides.)
  const bsLinesRepresented = [];
  const isCashName = (a) => {
    const t = String(a?.qbo_type || '').toLowerCase();
    if (t === 'bank') return true;
    if (t === 'other current asset' && /undeposited/i.test(a.name || '')) return true;
    return false;
  };
  const accById = {};
  for (const a of accounts) accById[String(a.id)] = a;
  const startField = hasPeriodStart ? 'period_start_balance' : 'prior_balance';
  const canonicalBsSections = ['assets_current', 'assets_fixed', 'assets_other',
                                'liabilities_current', 'debt_short',
                                'liabilities_long', 'equity'];
  const walkLeafForRepr = (sectionKey, leaf) => {
    let cur = 0, start = 0, hasReal = false;
    let isSyntheticNI = false;
    let anyNonCash = false;
    const sign = SECTION_SIGN[sectionKey] ?? 1;
    for (const aid of (leaf.accounts || [])) {
      const a = accById[String(aid)];
      if (!a) continue;
      if (sectionKey === 'equity' && /^net\s+income/i.test(a.name || '')) isSyntheticNI = true;
      if (!isCashName(a)) anyNonCash = true;
      cur   += sign * (Number(a.balance) || 0);
      start += sign * (Number(a[startField]) || 0);
      hasReal = true;
    }
    if (!hasReal || isSyntheticNI || !anyNonCash) return;
    const d = cur - start;
    if (Math.abs(d) < 0.5) return;
    bsLinesRepresented.push({ section: sectionKey, label: leaf.label });
  };
  const walkLineForRepr = (sectionKey, line) => {
    if (!line) return;
    if (line.type === 'group') {
      for (const c of (line.children || [])) walkLineForRepr(sectionKey, c);
      return;
    }
    walkLeafForRepr(sectionKey, line);
  };
  for (const sectionKey of canonicalBsSections) {
    for (const item of (mapping?.[sectionKey] || [])) {
      walkLineForRepr(sectionKey, item);
    }
  }
  // Also keep the V21 per-row labels so any downstream caller that
  // matches against V21's presentation labels (e.g. a future tooltip)
  // still finds them. These are appended; the canonical-named entries
  // are what the validation tie cares about.
  const sectionRowLabels = [
    ...v21.sections.operating.rows.map(r => ({ section: 'operating', label: r.label })),
    ...v21.sections.investing.rows.map(r => ({ section: 'investing', label: r.label })),
    ...v21.sections.financing.rows.map(r => ({ section: 'financing', label: r.label })),
  ];
  bsLinesRepresented.push(...sectionRowLabels);

  // Supplemental disclosures (ASC 230) — V21 surfaces from plPeriod.
  // For payable-net adjustments we keep the V20 behavior: report the
  // accrual-basis expense net of payable movement.
  const supplemental = {
    interestPaid: Math.abs(v21.supplemental.interestPaid) >= 0.5
      ? v21.supplemental.interestPaid : 0,
    taxesPaid:    Math.abs(v21.supplemental.taxesPaid)    >= 0.5
      ? v21.supplemental.taxesPaid    : 0,
  };

  // Unclassified advisories — V20 had its own; V21 surfaces ones with
  // non-zero delta. Reshape to the V20 label/qbo_type/delta shape.
  const unclassifiedAccountAdvisories = v21.unclassified
    .filter(u => Math.abs(u.delta) >= 0.5)
    .map(u => ({
      account_id:    u.id,
      account_name:  u.name,
      qbo_type:      u.qbo_type,
      classification: u.classification,
      delta:         u.delta,
      advisory:      `Account "${u.name}" (qbo_type=${u.qbo_type}) has a ` +
                     `non-zero period delta of ${u.delta.toFixed(2)} but was ` +
                     `not classified into Op/Inv/Fin. Likely a P&L account ` +
                     `appearing in BS query, or a custom qbo_type — verify ` +
                     `in QBO.`,
    }));

  // Bookkeeping anomaly advisory (Day 3 acceptance criterion):
  // surfaces when the IS-reported D&A diverges from the BS AccumDep
  // delta by > max($1,000, 1% of period revenue). The V21 result
  // already exposes `dAndA_IS`, `dAndA_BS`, `dAndA_variance`.
  // V21 carries them through; rendering happens in StaffDashboard.js
  // / PrintModal.js as a small inline banner above the CF.

  // ─── CF DISPLAY FOOTING — applied at RENDER time only ──────────────
  //
  // The renderer (PrintModal.renderCashFlow) calls `prepareCfForDisplay(cf)`
  // before drawing the CF. That helper applies cross-section
  // largest-remainder rounding to every row so:
  //   - Each section subtotal = sum of its displayed rows
  //   - NET CHANGE = sum of section subtotals
  //   - ENDING CASH = BEGIN CASH + NET CHANGE = BS Cash & Equivalents
  // AND runs a hard footing assertion (throws if printed totals don't
  // foot). Validation ties consume this same builder output but don't
  // care about display-rounding, so the redistribute is deferred until
  // render to keep both paths working.

  return {
    hasPrior,
    hasPeriodStart,
    netIncome: v21.netIncome,
    dAndA: v21.dAndA,
    dAndA_IS: v21.dAndA_IS,
    dAndA_BS: v21.dAndA_BS,
    dAndA_variance: v21.dAndA_variance,
    threshold,
    thresholdAutoComputed,
    niAtPeriodStart: opts.niAtPeriodStart != null
      ? Number(opts.niAtPeriodStart) : Number(niPriorExact) || 0,
    sections: {
      operating: v21.sections.operating,
      investing: v21.sections.investing,
      financing: v21.sections.financing,
      other: v21.unclassified.length > 0
        ? { rows: [], subtotal: 0 }  // V19.11 surface — V21 funnels via advisories
        : null,
    },
    supplemental,
    netChange: v21.netChange,
    beginCash: v21.beginCash,
    endCash:   v21.endCash,
    bsLinesRepresented,
    bsAccountsRepresented: bsLinesRepresented,
    nonCashBSDeltaTotal: 0,  // V20 surfaced this; V21 has identity by
                              // construction so the diagnostic is moot.
    unclassifiedAccountAdvisories,
    // V21 diagnostic surface — exposed for debugging only, not rendered.
    _v21: {
      identityDelta:  v21.identityDelta,
      identityPasses: v21.identityPasses,
      sectionsNet:    v21.sectionsNet,
      builder:        v21.builder,
    },
  };
}


// ─── CF DISPLAY-FOOTING RECONCILIATION (RENDER-TIME) ────────────────
//
// Idempotent. Call ONCE on a CF object before rendering. Applies
// cross-section largest-remainder rounding so:
//   - Each section subtotal = sum of its rounded rows (foots on face)
//   - NET CHANGE = sum of section subtotals
//   - ENDING CASH = BEGIN CASH + NET CHANGE
//   - ENDING CASH ties to BS Cash & Equivalents (both = round0(sum of
//     canonical bank balances))
//
// V18 (2026-06-10) — single-source NI rounding. The Net Income row
// (always the first operating row, labeled "Net Income") is PINNED
// to round0(its exact value) and EXCLUDED from the redistribute pool.
// The remaining-rows residual absorbs the cents drift instead. This
// guarantees the printed CF Net Income line equals round0(IS Net
// Income) and equals the BS equity NI line, eliminating the $1
// divergence pattern the largest-remainder algorithm used to produce
// when NI happened to be the largest |value| row in the section.
// `ni_displayed_consistent` validation tie enforces this; PDF
// generation blocks if a divergence escapes.
//
// Returns the same object (mutated in place) for caller convenience.
export function prepareCfForDisplay(cf) {
  if (!cf || !cf.sections || cf.__displayPrepared) return cf;
  const roundedBeginCash = Math.round(Number(cf.beginCash) || 0);
  const roundedEndCash   = Math.round(Number(cf.endCash)   || 0);
  const targetNetChange  = roundedEndCash - roundedBeginCash;

  // Collect flattened rows + identify the pinned Net Income row(s).
  // The CF builder emits "Net Income" as kind='subheader-first' at
  // the top of the Operating section. Any row labeled "Net Income"
  // is pinned; the pin uses round0(exact value).
  const rowsFlat = [];
  const isPinned = (r) =>
    (r.kind === 'subheader-first' && r.label === 'Net Income') ||
    r.label === 'Net Income';

  // Pre-pin: stamp pinned rows with round0(value) and freeze them.
  for (const sec of ['operating', 'investing', 'financing']) {
    const sObj = cf.sections[sec];
    if (!sObj || !sObj.rows) continue;
    for (const r of sObj.rows) {
      if (r.kind === 'rollup' && r.value == null) continue;
      if (isPinned(r)) {
        r.value = Math.round(Number(r.value) || 0);
      } else {
        rowsFlat.push(r);
      }
    }
  }

  // Pinned total — already integer; subtract from target so the
  // residual is distributed only across un-pinned rows.
  let pinnedSum = 0;
  for (const sec of ['operating', 'investing', 'financing']) {
    for (const r of (cf.sections[sec]?.rows || [])) {
      if (isPinned(r)) pinnedSum += Number(r.value) || 0;
    }
  }
  const targetNonPinned = targetNetChange - pinnedSum;

  if (rowsFlat.length > 0) {
    const exactValues = rowsFlat.map(r => Number(r.value) || 0);
    const roundedValues = roundLargestRemainderToTarget(exactValues, targetNonPinned);
    rowsFlat.forEach((r, i) => { r.value = roundedValues[i]; });
  }
  for (const sec of ['operating', 'investing', 'financing']) {
    const sObj = cf.sections[sec];
    if (!sObj) continue;
    sObj.subtotal = (sObj.rows || [])
      .reduce((s, r) => s + (Number(r.value) || 0), 0);
  }
  cf.netChange = targetNetChange;
  cf.beginCash = roundedBeginCash;
  cf.endCash   = roundedEndCash;
  // V18 — propagate the rounded NI to the top-level netIncome too, so
  // any downstream consumer reading cf.netIncome gets the same int the
  // face uses. (The CF builder originally stored the raw float here.)
  const cfNIRow = cf?.sections?.operating?.rows?.find(r => r.label === 'Net Income');
  if (cfNIRow != null) cf.netIncome = Number(cfNIRow.value) || 0;
  cf.__displayPrepared = true;
  return cf;
}

// ─── RENDER-TIME FOOTING ASSERTIONS ─────────────────────────────────
//
// Hard guards. Throw with a structured diagnostic if any printed total
// fails to equal the sum of its printed (rounded) children. Renderer
// (PrintModal) MUST call before stamping the PDF; surfaces inline as a
// blocking error rather than shipping a non-footing face.
export function assertCfFootsForRender(cf) {
  if (!cf || !cf.sections) return;
  const sumSec = (s) => (s?.rows || []).reduce((acc, r) =>
    acc + ((r.kind === 'rollup' && r.value == null) ? 0 : (Number(r.value) || 0)), 0);
  const opSum = sumSec(cf.sections.operating);
  const invSum = sumSec(cf.sections.investing);
  const finSum = sumSec(cf.sections.financing);
  const failures = [];
  if (opSum !== cf.sections.operating.subtotal)
    failures.push(`Operating: rows sum to ${opSum}, displayed subtotal ${cf.sections.operating.subtotal}`);
  if (invSum !== cf.sections.investing.subtotal)
    failures.push(`Investing: rows sum to ${invSum}, displayed subtotal ${cf.sections.investing.subtotal}`);
  if (finSum !== cf.sections.financing.subtotal)
    failures.push(`Financing: rows sum to ${finSum}, displayed subtotal ${cf.sections.financing.subtotal}`);
  const sectionTotal = cf.sections.operating.subtotal + cf.sections.investing.subtotal + cf.sections.financing.subtotal;
  if (sectionTotal !== cf.netChange)
    failures.push(`NET CHANGE: displayed ${cf.netChange}, sum of section subtotals ${sectionTotal}`);
  if ((cf.beginCash + cf.netChange) !== cf.endCash)
    failures.push(`ENDING CASH: displayed ${cf.endCash}, BEGIN+NET CHANGE = ${cf.beginCash + cf.netChange}`);
  if (failures.length > 0) {
    const err = new Error('CF render blocked: printed totals do not foot.\n  ' + failures.join('\n  '));
    err.diagnostic = { opSum, invSum, finSum, sectionTotal, ...cf };
    throw err;
  }
}

export function assertBsFootsForRender(bs) {
  if (!bs || !bs.sections || !bs.totals) return;
  const s = bs.sections;
  const t = bs.totals;
  const p = bs.prior;
  const failures = [];
  const sumAssetsCur  = s.cash.total       + s.assetsCur.total       + s.assetsFix.total       + s.assetsOther.total;
  const sumAssetsPri  = (s.cash.total_prior||0) + (s.assetsCur.total_prior||0) + (s.assetsFix.total_prior||0) + (s.assetsOther.total_prior||0);
  const sumLiabCur    = s.liabCur.total       + s.debtShort.total       + s.liabLong.total;
  const sumLiabPri    = (s.liabCur.total_prior||0) + (s.debtShort.total_prior||0) + (s.liabLong.total_prior||0);
  const sumEquityCur  = (s.equity.lines || []).reduce((a, l) => a + (l.current || 0), 0);
  const sumEquityPri  = (s.equity.lines || []).reduce((a, l) => a + (l.prior   || 0), 0);
  if (t.totalAssets !== sumAssetsCur)
    failures.push(`TOTAL ASSETS (current): displayed ${t.totalAssets}, sum of section subtotals ${sumAssetsCur}`);
  if (t.totalLiab !== sumLiabCur)
    failures.push(`TOTAL LIABILITIES (current): displayed ${t.totalLiab}, sum of section subtotals ${sumLiabCur}`);
  if (t.totalEquityBeforeNI !== sumEquityCur)
    failures.push(`Total Equity-before-NI (current): displayed ${t.totalEquityBeforeNI}, sum of equity lines ${sumEquityCur}`);
  if (t.totalEquity !== (sumEquityCur + t.netIncome))
    failures.push(`TOTAL EQUITY (current): displayed ${t.totalEquity}, equity lines + NI = ${sumEquityCur + t.netIncome}`);
  if (t.totalLE !== (t.totalLiab + t.totalEquity))
    failures.push(`TOTAL L+E (current): displayed ${t.totalLE}, totalLiab + totalEquity = ${t.totalLiab + t.totalEquity}`);
  if (t.totalAssets !== t.totalLE)
    failures.push(`BS face out of balance (current): TOTAL ASSETS ${t.totalAssets} !== TOTAL L+E ${t.totalLE}`);
  if (p) {
    if (p.totalAssets !== sumAssetsPri)
      failures.push(`TOTAL ASSETS (prior): displayed ${p.totalAssets}, sum of section subtotals ${sumAssetsPri}`);
    if (p.totalLiab !== sumLiabPri)
      failures.push(`TOTAL LIABILITIES (prior): displayed ${p.totalLiab}, sum of section subtotals ${sumLiabPri}`);
    if (p.totalEquityBeforeNI !== sumEquityPri)
      failures.push(`Total Equity-before-NI (prior): displayed ${p.totalEquityBeforeNI}, sum of equity lines ${sumEquityPri}`);
    if (p.totalEquity !== (sumEquityPri + p.netIncome))
      failures.push(`TOTAL EQUITY (prior): displayed ${p.totalEquity}, equity lines + NI = ${sumEquityPri + p.netIncome}`);
    if (p.totalLE !== (p.totalLiab + p.totalEquity))
      failures.push(`TOTAL L+E (prior): displayed ${p.totalLE}, totalLiab + totalEquity = ${p.totalLiab + p.totalEquity}`);
    if (p.totalAssets !== p.totalLE)
      failures.push(`BS face out of balance (prior): TOTAL ASSETS ${p.totalAssets} !== TOTAL L+E ${p.totalLE}`);
  }
  if (failures.length > 0) {
    const err = new Error('BS render blocked: printed totals do not foot.\n  ' + failures.join('\n  '));
    err.diagnostic = { sums: { sumAssetsCur, sumAssetsPri, sumLiabCur, sumLiabPri, sumEquityCur, sumEquityPri }, bs };
    throw err;
  }
}


// Build the canonical Trailing 12 Months P&L matrix from QBO per-account
// monthly data + canonical mapping. Each mapped IS line becomes a row with
// 12 monthly values + a total. The mapping is the same one used by the
// single-period IS, so the T12 rows match the IS rows label-for-label.
//
// Inputs:
//   accountsMonthly: [{id, months: [12 floats], total, bucket}]
//   mapping: canonical CoA
//   ttmMonthLabels: ['Jan 25', 'Feb 25', ...] in column order
//
// Output:
//   { rows: structured row stream with kind: 'section'|'group'|'leaf'|'subtotal'|'margin',
//     months: 12 column labels, totals: {revenue, cogs, gross_profit, opex, other_income,
//     other_expense, net_income}, margin: [12 floats or null], netMarginTotal }
// T12 — trailing-twelve-month income statement. Renders from the
// canonical mapping; rows match the IS row-for-row.
//
// V18.1 (2026-06-10) — UNIFIED-SOURCE T12.
//
// Old behavior (pre-V18.1): the T12 builder consumed the legacy
// `accountsMonthly` array (sign-flipped credit-natural buckets from
// `/api/qbo/ttm`), walked `mapping[sectionKey]` independently, and
// summed monthly values with `SECTION_SIGN[sectionKey]`. The result:
//
//   • No cross-section reclassification — accounts canonically moved
//     to a different section produced different totals between IS and
//     T12 for the same caption (Rent & Occupancy net of $14,219 in
//     the IS; gross on the T12).
//   • No V22.2 Header.ColData parent-account own-dollars capture —
//     parent accounts with direct postings (Automobile Expense root,
//     Insurance root, Computer Expenses root) were missing from
//     `accounts_monthly`, so the T12 opex was lower than the IS opex
//     by the sum of those parent dollars (~$35,702 across SMJ Jan-May
//     2026).
//
// New behavior (V18.1): the backend `/api/qbo/ttm` endpoint now emits
// `detail_lines_monthly` (per-account 12-month arrays in the V22.2
// parser shape, including parent-own-dollars), per-month
// `section_totals_monthly`, and per-month `net_income_monthly`. This
// builder slices each month into a synthetic `plPeriod` object and
// runs `buildIncomeStatementV22(accounts, mapping, monthPL, null)`
// per month — inheriting V18 cross-section contra reclass and the
// V22.2 parser fix in one stroke. The T12 row matrix is built from
// the union of leaves emitted across all 12 monthly V22 runs (keyed
// on `qbo_account_id × canonical_section` so an account active in
// only some months still gets a stable row with $0 elsewhere). Net
// Income per month is sourced from `monthV22.totals.netIncome`,
// guaranteeing T12 monthly NI sums to IS YTD NI by construction.
// The `t12_ni_ties_to_is_ytd` validation gate enforces this — fails
// closed on any divergence ≥ $0.01 of unrounded values.
//
// Inputs:
//   accounts:       CoA list (passed through to buildIncomeStatementV22)
//   mapping:        canonical CoA mapping
//   ttmData:        the FULL `/api/qbo/ttm` response object
//   ttmMonthLabels: ['Jun 25', 'Jul 25', ..., 'May 26'] in column order
//
// Output: same row shape as before (months/sections/totals), with
// rows now derived from buildIncomeStatementV22 monthly runs.
export function buildT12(accounts, mapping, ttmData, ttmMonthLabels = []) {
  // ── Legacy-callsite compatibility ────────────────────────────────
  // Old callers pass (accountsMonthly_array, mapping, monthLabels).
  // Detect that shape and re-pack into the new signature so dashboard
  // KPI consumers don't break during the migration.
  if (Array.isArray(accounts) && accounts.length > 0 && accounts[0]
      && Object.prototype.hasOwnProperty.call(accounts[0], 'months')
      && Object.prototype.hasOwnProperty.call(accounts[0], 'bucket')) {
    // accounts here IS actually accountsMonthly; mapping is right;
    // ttmData here is actually the month-labels array.
    const legacyAccountsMonthly = accounts;
    const legacyMonthLabels = Array.isArray(ttmData) ? ttmData : [];
    return _buildT12_legacy(legacyAccountsMonthly, mapping, legacyMonthLabels);
  }
  // ── V18.1 unified path ───────────────────────────────────────────
  const detailLinesMonthly = ttmData?.detail_lines_monthly;
  const sectionTotalsMonthly = ttmData?.section_totals_monthly;
  const netIncomeMonthly = ttmData?.net_income_monthly;
  if (!Array.isArray(detailLinesMonthly) || !Array.isArray(sectionTotalsMonthly)) {
    // Backend hasn't been upgraded — fall back to the legacy path with
    // the old accountsMonthly array shape if available.
    return _buildT12_legacy(ttmData?.accounts_monthly || [], mapping, ttmMonthLabels);
  }
  const n = ttmMonthLabels.length || sectionTotalsMonthly.length || 12;

  // Build 12 per-month V22 outputs.
  const monthlyV22 = [];
  for (let mi = 0; mi < n; mi++) {
    // Slice each detail_line's months[mi] into a synthetic plPeriod
    // detail_line of the YTD shape buildIncomeStatementV22 expects.
    const monthDetailLines = [];
    for (const dl of detailLinesMonthly) {
      const v = Number(dl.months?.[mi]) || 0;
      if (Math.abs(v) < 0.005) continue;
      monthDetailLines.push({
        section: dl.section,
        name: dl.name,
        qbo_account_id: dl.qbo_account_id,
        value: v,
      });
    }
    const st = sectionTotalsMonthly[mi] || {};
    const monthPL = {
      section_totals: {
        Income:        Number(st.Income)        || 0,
        COGS:          Number(st.COGS)          || 0,
        Expenses:      Number(st.Expenses)      || 0,
        OtherIncome:   Number(st.OtherIncome)   || 0,
        OtherExpenses: Number(st.OtherExpenses) || 0,
        GrossProfit:   Number(st.GrossProfit) || (Number(st.Income||0) - Number(st.COGS||0)),
      },
      detail_lines: monthDetailLines,
      net_income: Number(netIncomeMonthly?.[mi]) || 0,
    };
    monthlyV22.push(buildIncomeStatementV22(accounts, mapping, monthPL, null));
  }

  // ── Union the leaves across all 12 months, keyed on
  //    (canonical_section × qbo_account_id × leaf_label).
  //
  //    Rationale: an account with activity in only some months still
  //    needs a stable row with $0 in months where it's absent. Without
  //    the union, the row would disappear from months where the leaf
  //    had no contribution. We also key on leaf_label so the same
  //    account appearing twice in a mapping (rare, but possible after
  //    a cross-section reclass) collapses correctly.
  //
  //    For each canonical section we walk monthlyV22[mi].sections[k]
  //    and collect leaves in order of first appearance, so the union
  //    preserves the mapping's natural display order.
  const sectionKeys = ['revenue', 'cogs', 'opex', 'otherIncome', 'otherExpense'];
  const unionLeaves = {};   // sectionKey → array of {label, accountIds[], months[12]}
  const unionGroups = {};   // sectionKey → array of {label, leaves: [...], monthsAcc}
  for (const sk of sectionKeys) unionLeaves[sk] = [];

  // First pass: discover leaves + their stable order from month 0,
  // then re-walk subsequent months filling values into the discovered
  // leaf slots. If a later month surfaces a leaf the first month
  // didn't (e.g. account first active in March), append it.
  const leafKey = (sectionLines, leaf) => {
    const ids = (leaf.accounts || []).slice().sort().join(',');
    return `${leaf.label}|${ids}`;
  };
  const leafIndex = {};  // sk → key → index in unionLeaves[sk]

  for (let mi = 0; mi < n; mi++) {
    const v22 = monthlyV22[mi];
    for (const sk of sectionKeys) {
      const lines = v22.sections[sk] || [];
      if (!leafIndex[sk]) leafIndex[sk] = {};
      for (const line of lines) {
        if (line.type !== 'leaf') continue;  // groups handled below
        const k = leafKey(sk, line);
        let idx = leafIndex[sk][k];
        if (idx === undefined) {
          unionLeaves[sk].push({
            label: line.label,
            accounts: (line.accounts || []).slice(),
            months: Array(n).fill(0),
            crossSign: line.crossSign || false,
          });
          idx = unionLeaves[sk].length - 1;
          leafIndex[sk][k] = idx;
        }
        unionLeaves[sk][idx].months[mi] = Number(line.current) || 0;
      }
    }
  }

  // Build per-section monthsTotal + total from the union.
  const sectionAgg = {};
  for (const sk of sectionKeys) {
    const monthsTotal = Array(n).fill(0);
    for (const leaf of unionLeaves[sk]) {
      for (let mi = 0; mi < n; mi++) monthsTotal[mi] += leaf.months[mi];
    }
    sectionAgg[sk] = {
      rows: unionLeaves[sk]
        .map(l => ({
          kind: 'leaf',
          label: l.label,
          months: l.months,
          total: l.months.reduce((s, v) => s + v, 0),
          crossSign: l.crossSign,
        }))
        .filter(r => Math.abs(r.total) >= 0.005),
      monthsTotal,
      total: monthsTotal.reduce((s, v) => s + v, 0),
    };
  }

  const revenue = sectionAgg.revenue;
  const cogs    = sectionAgg.cogs;
  const opex    = sectionAgg.opex;
  const oInc    = sectionAgg.otherIncome;
  const oExp    = sectionAgg.otherExpense;

  // Gross / Net per month sourced from V22 outputs directly so the
  // T12 monthly NI exactly equals the per-month V22 NI (single source).
  const grossMonths = monthlyV22.map(v => v.totals.grossProfit);
  const netMonthsUnrounded = monthlyV22.map(v => v.totals.netIncome);
  const grossTotal = grossMonths.reduce((s, v) => s + v, 0);
  const netTotalUnrounded = netMonthsUnrounded.reduce((s, v) => s + v, 0);

  // V18.1 display-rounding for the NI row only.
  //
  // The gate `t12_ni_ties_to_is_ytd` already enforces unrounded
  // equality between T12 monthly NI sum (in-period months) and IS
  // YTD NI. But the PDF DISPLAYS each monthly value rounded to the
  // dollar, and rounding 5 monthly cents-bearing floats independently
  // can leave their displayed sum ±$1 off the displayed YTD NI
  // (e.g. SMJ Jan-May 2026: 5 months at .29, .02, .54, .66, -.33
  //  round individually to a sum of 599,880 vs YTD round = 599,879).
  //
  // To preserve "T12 columns sum to IS YTD NI exactly" on the
  // rendered face WITHOUT touching the gate (gate stays unrounded):
  //   1. Compute IS YTD NI rounded target = round0(YTD unrounded
  //      sum across in-period months). Caller passes
  //      `displayWindow.inPeriodIndices` so we know which monthly
  //      cells participate in the YTD figure the user reads.
  //   2. Apply largest-remainder rounding ONLY to those in-period
  //      monthly NI cells, targeting that integer sum.
  //   3. Months outside the in-period window keep their natural
  //      round0 (they don't participate in the IS YTD tie).
  //
  // If `displayWindow` is not supplied (older callers), every monthly
  // NI rounds independently — same behavior as pre-V18.1, plus a
  // potential ±$1 drift that the gate logs but doesn't block (the
  // gate compares unrounded). PrintModal callers SHOULD supply the
  // window so the displayed face is footed.
  const netMonths = netMonthsUnrounded.slice();
  // (we accept displayWindow through the 5th positional arg below;
  // this slot remains backward-compatible)
  return _finalizeBuildT12({
    n,
    ttmMonthLabels,
    monthlyV22,
    revenue, cogs, opex, oInc, oExp,
    grossMonths, grossTotal,
    netMonthsUnrounded, netTotalUnrounded,
    displayWindow: arguments.length > 4 ? arguments[4] : null,
  });
}

function _finalizeBuildT12({ n, ttmMonthLabels, monthlyV22, revenue, cogs, opex, oInc, oExp,
                              grossMonths, grossTotal, netMonthsUnrounded, netTotalUnrounded,
                              displayWindow }) {
  const netMonths = netMonthsUnrounded.slice();
  if (displayWindow && Array.isArray(displayWindow.inPeriodIndices) && displayWindow.inPeriodIndices.length > 0) {
    const idxs = displayWindow.inPeriodIndices.filter((i) => i >= 0 && i < n);
    const inPeriodValues = idxs.map((i) => netMonthsUnrounded[i]);
    const targetInPeriod = Math.round(inPeriodValues.reduce((s, v) => s + v, 0));
    const rounded = roundLargestRemainderToTarget(inPeriodValues, targetInPeriod);
    idxs.forEach((i, k) => { netMonths[i] = rounded[k]; });
    for (let i = 0; i < n; i++) {
      if (!idxs.includes(i)) netMonths[i] = Math.round(netMonthsUnrounded[i]);
    }
  }
  const netTotal = netMonths.reduce((s, v) => s + v, 0);

  // Net Margin per month — guard divide-by-zero. Uses unrounded V22
  // for accuracy; the percent is computed from unrounded float values.
  const marginMonths = monthlyV22.map(v => {
    const rev = v.totals.totalRevenue;
    return Math.abs(rev) < 0.005 ? null : (v.totals.netIncome / rev) * 100;
  });
  const marginTotal = Math.abs(revenue.total) < 0.005
    ? null
    : (netTotalUnrounded / revenue.total) * 100;

  return {
    months: ttmMonthLabels.length > 0 ? ttmMonthLabels : Array.from({ length: n }, (_, i) => `M${i+1}`),
    sections: [
      { label: 'Revenue', rows: revenue.rows, monthsTotal: revenue.monthsTotal, total: revenue.total },
      { label: 'Cost of Goods Sold', rows: cogs.rows, monthsTotal: cogs.monthsTotal, total: cogs.total, sign: 'neg' },
      { label: 'Gross Profit', isSubtotal: true, monthsTotal: grossMonths, total: grossTotal },
      { label: 'Operating Expenses', rows: opex.rows, monthsTotal: opex.monthsTotal, total: opex.total, sign: 'neg' },
      ...(oInc.rows.length > 0 ? [{ label: 'Other Income', rows: oInc.rows, monthsTotal: oInc.monthsTotal, total: oInc.total }] : []),
      ...(oExp.rows.length > 0 ? [{ label: 'Other Expense', rows: oExp.rows, monthsTotal: oExp.monthsTotal, total: oExp.total, sign: 'neg' }] : []),
      { label: 'Net Income', isSubtotal: true, monthsTotal: netMonths, total: netTotal },
      { label: 'Net Margin', isMargin: true, monthsTotal: marginMonths, total: marginTotal },
    ],
    totals: {
      revenue: revenue.total, cogs: cogs.total, gross_profit: grossTotal,
      opex: opex.total, other_income: oInc.total, other_expense: oExp.total,
      net_income: netTotal, net_margin: marginTotal,
    },
    // V18.1 — expose unrounded monthly V22 outputs so the
    // t12_ni_ties_to_is_ytd validation tie can compare unrounded NI
    // to IS YTD NI by summing monthly NI for in-period months.
    _v22Monthly: monthlyV22.map(v => ({ netIncome: v.totals.netIncome })),
  };
}

// Legacy T12 builder — kept as a fallback for older `/api/qbo/ttm`
// responses that don't include `detail_lines_monthly`. Same behavior
// as pre-V18.1 (no cross-section reclass, no parent-own-dollars
// capture). Should be unreachable in production once the backend
// upgrade is deployed.
function _buildT12_legacy(accountsMonthly, mapping, ttmMonthLabels = []) {
  const byId = {};
  (accountsMonthly || []).forEach(a => { byId[String(a.id)] = a; });
  const n = ttmMonthLabels.length || 12;
  const sumLineMonths = (sectionKey, line) => {
    const sign = SECTION_SIGN[sectionKey] ?? 1;
    if (line && line.type === 'group') {
      const cms = (line.children || []).map(child => sumLineMonths(sectionKey, child));
      const months = Array.from({ length: n }, (_, mi) =>
        cms.reduce((s, c) => s + (c.months[mi] || 0), 0));
      const total = months.reduce((s, v) => s + v, 0);
      return { months, total };
    }
    const ids = (line.accounts || []).map(String);
    const months = Array.from({ length: n }, (_, mi) =>
      sign * ids.reduce((s, id) => {
        const a = byId[id];
        return a ? s + (a.months[mi] || 0) : s;
      }, 0));
    const total = months.reduce((s, v) => s + v, 0);
    return { months, total };
  };
  const buildSectionRows = (sectionKey) => {
    const items = mapping?.[sectionKey] || [];
    const out = [];
    const monthsAcc = Array(n).fill(0);
    items.forEach(item => {
      if (item && item.type === 'group') {
        const groupMonthsAcc = Array(n).fill(0);
        const groupLeaves = [];
        (item.children || []).forEach(child => {
          const { months, total } = sumLineMonths(sectionKey, child);
          if (Math.abs(total) < 0.005) return;
          groupLeaves.push({ kind: 'leaf', label: child.label, months, total });
          months.forEach((v, mi) => { groupMonthsAcc[mi] += v; monthsAcc[mi] += v; });
        });
        if (groupLeaves.length > 0) {
          out.push({ kind: 'group_header', label: item.label });
          out.push(...groupLeaves);
          out.push({ kind: 'group_subtotal', label: `Total ${item.label}`,
                     months: groupMonthsAcc,
                     total: groupMonthsAcc.reduce((s, v) => s + v, 0) });
        }
      } else {
        const { months, total } = sumLineMonths(sectionKey, item);
        if (Math.abs(total) < 0.005) return;
        out.push({ kind: 'leaf', label: item.label, months, total });
        months.forEach((v, mi) => { monthsAcc[mi] += v; });
      }
    });
    return { rows: out, monthsTotal: monthsAcc, total: monthsAcc.reduce((s, v) => s + v, 0) };
  };
  const revenue = buildSectionRows('income');
  const cogs    = buildSectionRows('cogs');
  const opex    = buildSectionRows('opex');
  const oInc    = buildSectionRows('other_income');
  const oExp    = buildSectionRows('other_expense');
  const grossMonths = Array.from({ length: n }, (_, mi) =>
    revenue.monthsTotal[mi] - cogs.monthsTotal[mi]);
  const netMonths = Array.from({ length: n }, (_, mi) =>
    grossMonths[mi] - opex.monthsTotal[mi]
    + oInc.monthsTotal[mi] - oExp.monthsTotal[mi]);
  const grossTotal = grossMonths.reduce((s, v) => s + v, 0);
  const netTotal   = netMonths.reduce((s, v) => s + v, 0);
  const marginMonths = Array.from({ length: n }, (_, mi) =>
    Math.abs(revenue.monthsTotal[mi]) < 0.005
      ? null
      : (netMonths[mi] / revenue.monthsTotal[mi]) * 100);
  const marginTotal = Math.abs(revenue.total) < 0.005
    ? null
    : (netTotal / revenue.total) * 100;
  return {
    months: ttmMonthLabels.length > 0 ? ttmMonthLabels : Array.from({ length: n }, (_, i) => `M${i+1}`),
    sections: [
      { label: 'Revenue', rows: revenue.rows, monthsTotal: revenue.monthsTotal, total: revenue.total },
      { label: 'Cost of Goods Sold', rows: cogs.rows, monthsTotal: cogs.monthsTotal, total: cogs.total, sign: 'neg' },
      { label: 'Gross Profit', isSubtotal: true, monthsTotal: grossMonths, total: grossTotal },
      { label: 'Operating Expenses', rows: opex.rows, monthsTotal: opex.monthsTotal, total: opex.total, sign: 'neg' },
      ...(oInc.rows.length > 0 ? [{ label: 'Other Income', rows: oInc.rows, monthsTotal: oInc.monthsTotal, total: oInc.total }] : []),
      ...(oExp.rows.length > 0 ? [{ label: 'Other Expense', rows: oExp.rows, monthsTotal: oExp.monthsTotal, total: oExp.total, sign: 'neg' }] : []),
      { label: 'Net Income', isSubtotal: true, monthsTotal: netMonths, total: netTotal },
      { label: 'Net Margin', isMargin: true, monthsTotal: marginMonths, total: marginTotal },
    ],
    totals: {
      revenue: revenue.total, cogs: cogs.total, gross_profit: grossTotal,
      opex: opex.total, other_income: oInc.total, other_expense: oExp.total,
      net_income: netTotal, net_margin: marginTotal,
    },
  };
}

// Variance helper — guards divide-by-zero and "no prior" cases. Returns null
// if a percent can't meaningfully be computed (caller renders an em dash).
export function variancePct(current, prior) {
  if (prior === undefined || prior === null) return null;
  if (Math.abs(prior) < 0.005) return null;
  return ((current - prior) / Math.abs(prior)) * 100;
}

// Validation ties — surface any failure as a non-blocking banner.
// Each tie returns { ok: bool, label, variance? } so the banner can list them.
//
// IMPORTANT: this is the publish gate. PrintModal does not enforce it (staff
// may print working files). But the Snapshot publish endpoint MUST refuse to
// publish anything until allGreen === true AND all draft flags are false.
// See Section 2.8 of the build brief.
//
// Draft flags per statement — the snapshot publish endpoint refuses to include
// ANY statement carrying a draft flag, regardless of whether numerical ties
// pass. This is the gate that prevents Pages 5-7 (BS PY, IS PY, T12) from
// riding along in a published package while their renderers are still being
// migrated to the canonical lib.
// V25.23 — is this period selection safe to fetch?
//
// The dashboard's custom range uses two <input type="date"> fields, which fire
// onChange on EVERY keystroke. Typing 2026 walks the year through 0002, 0020,
// 0202 — and each of those half-typed windows was fetched. QuickBooks answers
// a nonsense window with HTTP 200 and an empty report, so whichever response
// landed last won: MEET Ministry ended up holding a package stamped
// 0002-07-31, and clicking "Custom" (which starts with both dates empty)
// fetched with no dates at all, which the backend resolves to the PRIOR MONTH
// — that is how a January-to-July selection printed an August package.
export function isUsableDashboardPeriod(period) {
  if (!period) return false;
  if (period.id !== 'custom') return true;   // presets compute their own range
  const plausible = (iso) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return false;
    const year = Number(iso.slice(0, 4));
    return year >= 1990 && year <= 2100;
  };
  return plausible(period.start) && plausible(period.end)
    && period.start <= period.end;
}

// V25.22 — one place that turns a failed QuickBooks pull into a sentence.
// Intuit reports its rate limit as a JSON fault body
// ("message=ThrottleExceeded; errorCode=003001; statusCode=429"), which is
// not something a CPA should have to read.
export function describeFetchFailure(name, v) {
  if (!v) return name;
  const raw = String(v.error || '');
  if (v.status === 429 || /ThrottleExceeded/i.test(raw)) {
    return `${name} — QuickBooks rate limit (throttle exceeded); it was retried and still refused`;
  }
  if (v.status === 401 || v.status === 403) {
    return `${name} — QuickBooks sign-in rejected (HTTP ${v.status})`;
  }
  const bits = [name];
  if (v.status) bits.push(`HTTP ${v.status}`);
  if (raw) bits.push(raw.slice(0, 160));
  if (Number.isFinite(v.ms)) bits.push(`${Math.round(v.ms)}ms`);
  return bits.join(' — ');
}

// V25.21 — is the validation result trustworthy yet?
//
// Changing the dashboard period refetches the accounts and the
// trailing-twelve-months pull as two separate requests, and the client-open
// flow loads the T12 later still. In the seconds between, the NEW window is
// judged against the PREVIOUS T12: every T12 tie fails, the banner goes red,
// and then it turns green on its own. Red has to mean a real failure, so the
// banner defers while a fetch is in flight or while the T12 on hand belongs
// to a different period end.
export function isPeriodUnsettled({ periodLoading = false, ttmLoading = false,
                                    qboMeta = null, ttmData = null,
                                    ttmError = null, selectedPeriod = null } = {}) {
  if (periodLoading || ttmLoading) return true;
  const periodEnd = qboMeta?.period_end || null;
  if (!periodEnd) return false;            // nothing period-scoped to judge
  // V25.23 — the figures on screen must belong to the period the user picked.
  // A custom range that is still being typed has no figures of its own yet,
  // and the previous period's data must not be judged (or issued) as if it
  // were the selection.
  if (selectedPeriod) {
    if (!isUsableDashboardPeriod(selectedPeriod)) return true;
    if (selectedPeriod.end && selectedPeriod.end !== periodEnd) return true;
  }
  if (!ttmData) return !ttmError;          // still on its way (an error is real)
  const ttmEnd = ttmData.period_end || null;
  return !!ttmEnd && ttmEnd !== periodEnd; // stale pairing
}


export function runValidationTies(accounts, mapping, ttmData, opts = {}) {
  // Build the IS first to get NI, then pass its exact NI float to BS so the
  // BS equity tie-up is consistent.
  const is = buildIncomeStatement(accounts, mapping, {
    showZeroes: true,
    plPeriod: opts.plPeriod,
    plPeriodPrior: opts.plPeriodPrior,
  });
  // V22 — exact NI sourced from the IS builder (which reads from QBO P&L
  // when plPeriod is provided, falls back to synthesis otherwise).
  const exactNI = is.totals.netIncome;
  const exactNIPrior = is.prior.netIncome;
  // V23.5 — the BS equity section carries fiscal-YTD NI, not period NI.
  const bsNI = bsNetIncomeAnchor(exactNI, opts.qboMeta);
  const bs = buildBalanceSheet(accounts, mapping, bsNI, exactNIPrior, { showZeroes: true });

  // Run BS balance ties on the DISPLAYED (rounded) figures — the artifact
  // that ships in the PDF. Current AND prior columns are both checked.
  const niCurrent = bsNI;
  const niPrior   = is.prior.netIncome;
  const displayedTotalEquityCurrent = bs.totals.totalEquityBeforeNI + niCurrent;
  const displayedTotalLECurrent     = bs.totals.totalLiab + displayedTotalEquityCurrent;
  // Prior column displayed equity = prior section subtotal + prior NI
  // We don't store a separate prior-equity-after-adjust; recompute from sections
  const equityPriorDisplayed = bs.sections.equity.lines.reduce((s, l) => s + l.prior, 0);
  const displayedTotalEquityPrior = equityPriorDisplayed + niPrior;
  const displayedTotalLEPrior     = bs.prior.totalLiab + displayedTotalEquityPrior;

  const ties = [
    {
      key: 'bs_balance',
      label: 'Balance Sheet balances on its face (Assets = Liab + Equity)',
      description: 'The fundamental accounting identity must hold on the rendered statement: Total Assets equals Total Liabilities + Total Equity, including Net Income retained for the period. A non-zero variance means a rounding leak, a missing equity line, or Net Income not flowing through to retained earnings on the face of the statement.',
      ok: Math.abs(bs.totals.totalAssets - displayedTotalLECurrent) < 1,
      variance: bs.totals.totalAssets - displayedTotalLECurrent,
    },
    {
      key: 'bs_balance_py',
      label: 'Prior-year Balance Sheet balances on its face',
      description: 'The prior-period Balance Sheet column must also balance (Assets = Liabilities + Equity). A failure here usually points to incomplete prior-period data ingestion or a prior_balance field not propagated to the equity section.',
      ok: Math.abs(bs.prior.totalAssets - displayedTotalLEPrior) < 1,
      variance: bs.prior.totalAssets - displayedTotalLEPrior,
    },
  ];

  // ─── V18 (2026-06-10): Section-residual gate (replaces in-line plugs) ──
  //
  // The Income Statement builder no longer prints "QBO P&L variance"
  // residual leaves on the rendered face. Any leftover residual after
  // cross-section reclassification + orphan-account deduction is a
  // mapping gap that MUST be resolved by editing the canonical
  // mapping. This tie fails closed and blocks PDF generation when:
  //   (a) any section's |residual| ≥ its materiality threshold (max
  //       of $500 or 0.5% of section total), OR
  //   (b) any orphan accounts are detected (present in QBO P&L but
  //       in no canonical leaf).
  // Failing diagnostics list every section over-tolerance and every
  // orphan account so the staff editor can route the fix.
  const isDiag = is?.diagnostic || {};
  const sectionResiduals = isDiag.sectionResiduals || {};
  const materialityBySection = isDiag.materialityBySection || {};
  const orphanAccounts = isDiag.orphanAccounts || [];
  const residualFails = [];
  for (const k of ['revenue','cogs','opex','otherIncome','otherExpense']) {
    const r = Number(sectionResiduals[k] || 0);
    const m = Number(materialityBySection[k] || 0);
    if (Math.abs(r) >= m && Math.abs(r) >= 1) {
      residualFails.push(`${k}: residual ${r.toFixed(2)} ≥ materiality ${m.toFixed(2)}`);
    }
  }
  if (orphanAccounts.length > 0) {
    residualFails.push(`${orphanAccounts.length} orphan account(s): ` +
      orphanAccounts.slice(0, 5).map(o => `${o.name} ($${o.value.toFixed(2)} in ${o.qboSection})`).join('; ') +
      (orphanAccounts.length > 5 ? `, +${orphanAccounts.length - 5} more` : ''));
  }
  ties.push({
    key: 'is_no_section_residual',
    label: 'No Income Statement section residuals (plugs eliminated)',
    description: 'Every IS section subtotal must equal the sum of its canonical mapping leaves after cross-section reclassification deltas are applied. Residuals above materiality (max $500 or 0.5% of section total) indicate an orphan account in the QBO P&L that is not assigned to any canonical leaf; PDF generation is blocked until the mapping is fixed. Render-time plug lines on the face of the statement are forbidden.',
    ok: residualFails.length === 0,
    variance: residualFails.length,
    diagnostic: {
      sectionResiduals,
      materialityBySection,
      orphanAccounts,
      failures: residualFails,
    },
  });

  // ─── V18 (2026-06-10): NI display consistency gate ──
  // (registered later in this function — see search for
  //  'ni_displayed_consistent' below, after `cf` is built. Pre-cf
  //  refs are forbidden here.)

  // ─── BS-PY column sources from QBO prior-year snapshot, never derived ──
  //
  // Defense-in-depth tie (2026-02-09). Catches any future regression where
  // a BS column total gets computed as Assets - Liab (plug) instead of
  // sourcing each line from the per-account `prior_balance` (May-31-2025
  // for an FY-2026 4/30/2026 period end). The plug pattern, if reintroduced,
  // would silently absorb data-path bugs into Partners' Equity and pass
  // `bs_balance_py` while shipping the wrong number on the face.
  //
  // Method: walk the same mapping sections + signs the BS builder uses,
  // summing `prior_balance` per mapped account. Cash is added canonically
  // (matches the cash IIFE in buildBalanceSheet — every Bank +
  // Undeposited Funds account, ignoring mapping). Synthetic QBO Net
  // Income equity row is excluded; the period NI is added once via
  // `niPrior`. These raw totals MUST equal the BS builder's column
  // totals to the dollar — the only way they diverge is if the builder
  // plugs.
  const accById = {};
  for (const a of (accounts || [])) accById[String(a.id)] = a;
  const isCashAcc = (a) => {
    const t = String(a?.qbo_type || '').toLowerCase();
    if (t === 'bank') return true;
    if (t === 'other current asset' && /undeposited/i.test(a?.name || '')) return true;
    return false;
  };
  const walkMappingPrior = (sec, sign, skipCash = false, skipSyntheticNI = false) => {
    let s = 0;
    const items = mapping?.[sec] || [];
    const walk = (it) => {
      if (!it) return;
      if (it.type === 'group') {
        for (const c of (it.children || [])) walk(c);
        return;
      }
      for (const aid of (it.accounts || [])) {
        const a = accById[String(aid)];
        if (!a) continue;
        if (skipCash && isCashAcc(a)) continue;
        if (skipSyntheticNI && /^net\s+income/i.test(a.name || '')) continue;
        s += sign * (Number(a.prior_balance) || 0);
      }
    };
    items.forEach(walk);
    return s;
  };
  // Cash via canonical rule (matches buildBalanceSheet cash IIFE).
  const rawCashPrior = (accounts || []).reduce((s, a) =>
    isCashAcc(a) ? s + (Number(a.prior_balance) || 0) : s, 0);
  // Non-cash mapped assets — skipCash defends against a cash account
  // mistakenly mapped into assets_current causing double-count (cash IIFE
  // counts it canonically, mapping leaf would count it again).
  const rawAssetsNonCashPrior =
      walkMappingPrior('assets_current', +1, /*skipCash*/ true)
    + walkMappingPrior('assets_fixed',   +1, /*skipCash*/ true)
    + walkMappingPrior('assets_other',   +1, /*skipCash*/ true);
  const rawAssetsPrior = rawCashPrior + rawAssetsNonCashPrior;
  const rawLiabPrior =
      walkMappingPrior('liabilities_current', -1)
    + walkMappingPrior('debt_short',          -1)
    + walkMappingPrior('liabilities_long',    -1);
  const rawEquityPrior = walkMappingPrior('equity', -1, /*skipCash*/ false, /*skipSyntheticNI*/ true);
  const rawLePrior = rawLiabPrior + rawEquityPrior + niPrior;
  // Tolerance matches the in-builder rounding adjustment cap. Sub-cent
  // largest-remainder drift across the {totalLiab, totalEquity, niPrior}
  // round0 triple naturally produces ±$1; the bounded ≤$2 redistribution
  // in buildBalanceSheet closes the bs_balance_py display, but pushes
  // raw snapshot lines $1 off here. Anything beyond $2 is a real data
  // error and surfaces red.
  const assetsPriorMatch = Math.abs(bs.prior.totalAssets   - round0(rawAssetsPrior)) <= 2;
  const lePriorMatch     = Math.abs(displayedTotalLEPrior - round0(rawLePrior))     <= 2;
  ties.push({
    key: 'bs_py_matches_prior_year_snapshot',
    label: 'Prior-year column totals match the prior-year-date QBO snapshot',
    description: 'Every total in the BS prior-year column (Assets and L+E) must equal the sum of per-account prior_balance values to the dollar. Any variance means a column total is being derived or plugged instead of sourced from the snapshot — a class of bug that silently corrupts the deliverable.',
    ok: assetsPriorMatch && lePriorMatch,
    variance: (bs.prior.totalAssets - round0(rawAssetsPrior)) + (displayedTotalLEPrior - round0(rawLePrior)),
    diagnostic: {
      bs_assets_prior:        bs.prior.totalAssets,
      raw_assets_prior:       round0(rawAssetsPrior),
      bs_le_prior:            displayedTotalLEPrior,
      raw_le_prior:           round0(rawLePrior),
      raw_cash_prior:         round0(rawCashPrior),
      raw_non_cash_assets_prior: round0(rawAssetsNonCashPrior),
      raw_liab_prior:         round0(rawLiabPrior),
      raw_equity_prior:       round0(rawEquityPrior),
      ni_prior:               niPrior,
    },
  });

  // Cross-statement tie: IS Net Income should equal the change in BS Total
  // Equity that's NOT explained by direct equity-section transactions (capital
  // contributions / draws on the books). On unrounded floats, this identity
  // is: (currentTotalEquity_inclNI - priorTotalEquity_inclNI_prior) -
  //       (equitySection_current - equitySection_prior) == niCurrent - niPrior
  // Because: TE_curr = equity_curr + NI_curr and TE_prior = equity_prior + NI_prior.
  // So this tie always holds algebraically — its purpose is to catch a future
  // regression where a renderer redefines Total Equity without including NI.
  const TE_curr  = displayedTotalEquityCurrent;
  const TE_prior = displayedTotalEquityPrior;
  const equityDeltaSection = bs.sections.equity.lines.reduce((s, l) => s + (l.current - l.prior), 0);
  const niDelta = niCurrent - niPrior;
  ties.push({
    key: 'ni_equity_tie',
    label: 'IS Net Income flows correctly into BS Total Equity',
    description: 'Net Income from the Income Statement must equal the change in Total Equity that is NOT explained by direct equity transactions (capital contributions, owner draws, distributions). A failure here means the Income Statement and Balance Sheet disagree about the period\'s earnings.',
    ok: Math.abs((TE_curr - TE_prior) - equityDeltaSection - niDelta) < 5,
    variance: (TE_curr - TE_prior) - equityDeltaSection - niDelta,
  });

  // QBO Balance Sheet reconciliation — the canary tie. The backend pulls
  // QBO's own /BalanceSheet for the period and our TB-derived canonical
  // BS totals MUST match to the dollar. If they don't, our ingestion has
  // dropped an account (deleted account, locked-period entry, etc.) and the
  // package cannot be trusted regardless of whether the canonical BS
  // internally foots A = L+E.
  const recon = opts.qboReconciliation;
  if (recon && recon.current) {
    const cv = recon.current.assets_variance || 0;
    const lv = recon.current.liab_equity_variance || 0;
    ties.push({
      key: 'qbo_bs_recon_current',
      label: 'Canonical Balance Sheet totals match QBO /BalanceSheet (current period)',
      description: 'Our canonical Balance Sheet (built from the QuickBooks Trial Balance) must match QuickBooks\' own Balance Sheet report for the same period to the dollar. A variance means our ingestion has dropped or duplicated an account (deleted accounts, locked-period entries, sub-account roll-up mismatches) and the published package cannot be trusted regardless of whether internal ties pass.',
      ok: !!recon.current.ok,
      variance: Math.abs(cv) >= Math.abs(lv) ? cv : lv,
    });
  }
  if (recon && recon.prior) {
    const cv = recon.prior.assets_variance || 0;
    const lv = recon.prior.liab_equity_variance || 0;
    ties.push({
      key: 'qbo_bs_recon_prior',
      label: 'Canonical Balance Sheet totals match QBO /BalanceSheet (prior period)',
      description: 'Same reconciliation as the current-period canary tie, applied to the prior period. A failure here usually points to incomplete prior-period TB ingestion or a deleted account that QBO still includes in its own Balance Sheet report.',
      ok: !!recon.prior.ok,
      variance: Math.abs(cv) >= Math.abs(lv) ? cv : lv,
    });
  }

  // Cash Flow ties — Cash Flow Statement must tie to:
  //   1. IS Net Income (cf_ni_ties_to_is)
  //   2. BS Cash & Equivalents at period end (cf_ending_cash_ties_to_bs)
  // When BOTH pass and `hasPrior` is true, the CF can lift its DRAFT flag.
  if (opts.cashFlowCanonical !== false) {
    // Caller may pre-compute CF and pass {cfNetIncome, cfEndingCash, bsCash}
    // — if not, derive via buildCashFlow.
  }
  const cf = (opts.cashFlow) || buildCashFlow(accounts, mapping, exactNI,
              sectionTotal(accounts, mapping, 'income', 'prior')
            - sectionTotal(accounts, mapping, 'cogs', 'prior')
            - sectionTotal(accounts, mapping, 'opex', 'prior')
            + sectionTotal(accounts, mapping, 'other_income', 'prior')
            - sectionTotal(accounts, mapping, 'other_expense', 'prior'));
  const bsCashCurrent = sectionTotal(accounts, mapping, 'cash', 'current');

  // CF ties evaluated on the canonical buildCashFlow output:
  //   - cf_ni_ties_to_is: Net Income on CF must equal IS Net Income.
  //   - cf_ending_cash_ties_to_bs: Ending Cash on CF must equal BS Cash &
  //     Equivalents at period end.
  // Both are gated by `hasPrior` since CF math is only meaningful with a
  // prior-period comparison.
  const cashFlowTiesPassing = (() => {
    if (!cf.hasPrior) return false;
    const niTie = Math.abs(cf.netIncome - exactNI) < 1;
    const cashTie = Math.abs(cf.endCash - bsCashCurrent) < 1;
    const sectionsTie = Math.abs(
      cf.sections.operating.subtotal + cf.sections.investing.subtotal +
      cf.sections.financing.subtotal - (cf.endCash - cf.beginCash)
    ) < 1;
    // V15.1: require period_start balances (else this is a V15-style
    // 12-month mismatch). Backend supplies period_start_balance for every
    // account; legacy data lacks it.
    const periodTie = cf.hasPeriodStart === true;
    return niTie && cashTie && sectionsTie && periodTie;
  })();
  if (cf.hasPrior) {
    ties.push({
      key: 'cf_ni_ties_to_is',
      label: 'Cash Flow Net Income ties to Income Statement Net Income',
      description: 'The opening line of the Statement of Cash Flows (Net Income) must equal the bottom line of the Income Statement to the dollar. Any drift here means the two statements are built on inconsistent Net Income figures.',
      ok: Math.abs(cf.netIncome - exactNI) < 1,
      variance: cf.netIncome - exactNI,
    });

    // ─── V18 (2026-06-10): NI display consistency gate ──
    // Net Income must be rendered identically across IS, BS, CF. The
    // IS computes NI from the QBO P&L; the BS consumes round0(IS.NI)
    // for its equity line; the CF's printed Net Income line must
    // equal round0(IS.NI) as well. `prepareCfForDisplay` pins the CF
    // NI row to this value and distributes the cents residual across
    // non-NI rows. A failing gate (typically $1 drift) indicates one
    // statement independently rounded NI instead of consuming the
    // canonical rounded value.
    const niDisplayed = Math.round(exactNI);
    const bsNIDisplayed = Math.round(bs.totals.netIncome);
    // V23.5 — the BS NI row is fiscal-YTD anchored; compare against the
    // anchored expectation, not the period NI. CF NI stays period-scoped.
    const bsNIExpected = Math.round(bsNI);
    const cfNIRow = cf?.sections?.operating?.rows?.find(r => r.label === 'Net Income');
    const cfNIDisplayed = cfNIRow != null
      ? Math.round(Number(cfNIRow.value) || 0)
      : Math.round(Number(cf?.netIncome) || 0);
    const niConsistent =
      bsNIDisplayed === bsNIExpected && cfNIDisplayed === niDisplayed;
    ties.push({
      key: 'ni_displayed_consistent',
      label: 'Net Income identical across IS, BS, CF',
      description: 'The Net Income figure printed on the Income Statement, Balance Sheet equity section, and Cash Flow Statement must all match to the dollar. A divergence (typically $1, from independent rounding paths) indicates the CF or BS rounded NI separately instead of consuming the canonical rounded value from the IS builder.',
      ok: niConsistent,
      variance: (cfNIDisplayed - niDisplayed) || (bsNIDisplayed - niDisplayed),
      diagnostic: {
        is_displayed: niDisplayed,
        bs_displayed: bsNIDisplayed,
        cf_displayed: cfNIDisplayed,
      },
    });
    ties.push({
      key: 'cf_ending_cash_ties_to_bs',
      label: 'Cash Flow Ending Cash ties to Balance Sheet Cash & Equivalents',
      description: 'The closing balance produced by the Statement of Cash Flows must equal the Cash & Equivalents line on the Balance Sheet at period end. A variance here means the cash reconciliation is incomplete — typically a missed working-capital or investing/financing item.',
      ok: Math.abs(cf.endCash - bsCashCurrent) < 1,
      variance: cf.endCash - bsCashCurrent,
    });

    // V15 / V15.1: structural ties for the canonical-line Cash Flow.
    //
    // 1) cf_section_subtotals_tie_to_bs_deltas — the indirect-method
    //    identity. Net of Operating + Investing + Financing must equal
    //    the change in cash on the BS (endCash - beginCash). This is
    //    the foundational "is the math correct" check.
    //
    // V15.2: emit detailed subtotals + bs_change_in_cash + variance so
    // a failing tie reveals WHICH section is mis-summed (previously you
    // only saw the aggregate variance — the V15.1 -$481,258 case required
    // a separate diagnostic pass to attribute the gap to financing).
    const opTotal  = cf.sections.operating.subtotal;
    const invTotal = cf.sections.investing.subtotal;
    const finTotal = cf.sections.financing.subtotal;
    const otherTotal = cf.sections.other?.subtotal || 0;
    const cfSectionsNet = opTotal + invTotal + finTotal + otherTotal;
    const bsCashChange = cf.endCash - cf.beginCash;
    ties.push({
      key: 'cf_section_subtotals_tie_to_bs_deltas',
      label: 'CF section subtotals (Op + Inv + Fin) tie to BS change in cash',
      description: 'The fundamental indirect-method identity: Operating + Investing + Financing section subtotals on the Statement of Cash Flows must equal the period-over-period change in Cash on the Balance Sheet to the dollar. A variance here means the canonical line set is not fully reconciling and the package has a missing cash-affecting item.',
      ok: Math.abs(cfSectionsNet - bsCashChange) < 1,
      variance: cfSectionsNet - bsCashChange,
      diagnostic: {
        op_total:  Number(opTotal.toFixed(2)),
        inv_total: Number(invTotal.toFixed(2)),
        fin_total: Number(finTotal.toFixed(2)),
        sections_net:        Number(cfSectionsNet.toFixed(2)),
        bs_change_in_cash:   Number(bsCashChange.toFixed(2)),
        begin_cash:          Number(cf.beginCash.toFixed(2)),
        end_cash:            Number(cf.endCash.toFixed(2)),
        ni_at_period_start:  cf.niAtPeriodStart,
        has_period_start:    cf.hasPeriodStart,
        operating_line_count: cf.sections.operating.rows.length,
        financing_line_count: cf.sections.financing.rows.length,
      },
    });

    // 2) cf_lines_reconcile_to_bs — structural coverage check. Walks the
    //    canonical mapping LINES (matching what buildCashFlow consumes in
    //    V15.1). Every line with a meaningful delta must be represented in
    //    the CF.
    const fullLineSet = new Set();
    const allBsSections = ['assets_current','assets_fixed','assets_other',
                           'liabilities_current','debt_short','liabilities_long','equity'];
    const accById2 = {};
    accounts.forEach(a => { accById2[String(a.id)] = a; });
    const startField = cf.hasPeriodStart ? 'period_start_balance' : 'prior_balance';
    // Match the canonical isCashAccount() rule used by buildCashFlowV21 +
    // walkLeafForRepr above so this walker's notion of "non-cash BS line"
    // is identical to the CF builder's. Mapping leaves that contain ONLY
    // bank/undeposited accounts are the Cash & Equivalents output of the
    // CF — never a working-capital input — and must be excluded here.
    const isCashAcc = (a) => {
      const t = String(a?.qbo_type || '').toLowerCase();
      if (t === 'bank') return true;
      if (t === 'other current asset' && /undeposited/i.test(a?.name || '')) return true;
      return false;
    };
    const walkSection = (sectionKey) => {
      const items = mapping?.[sectionKey] || [];
      const sign = SECTION_SIGN[sectionKey] ?? 1;
      const walkLeaf = (leaf) => {
        let cur = 0, start = 0, hasReal = false;
        let isSyntheticNI = false;
        let anyNonCash = false;
        (leaf.accounts || []).forEach(aid => {
          const a = accById2[String(aid)];
          if (!a) return;
          if (sectionKey === 'equity' && /^net\s+income/i.test(a.name || '')) isSyntheticNI = true;
          if (!isCashAcc(a)) anyNonCash = true;
          cur   += sign * (a.balance || 0);
          start += sign * (a[startField] || 0);
          hasReal = true;
        });
        if (!hasReal || isSyntheticNI || !anyNonCash) return;
        const d = cur - start;
        if (Math.abs(d) >= 0.5) fullLineSet.add(`${sectionKey}::${leaf.label}`);
      };
      const w = (line) => {
        if (!line) return;
        if (line.type === 'group') { (line.children || []).forEach(w); return; }
        walkLeaf(line);
      };
      items.forEach(w);
    };
    allBsSections.forEach(walkSection);
    const represented = new Set((cf.bsLinesRepresented || []).map(r => {
      // Try to find which section this line belongs to.
      return r.section + '::' + r.label;
    }));
    // Be permissive: represented may use section tag like 'operating' rather
    // than 'assets_current'. Allow match-by-label alone if section tag differs.
    const representedLabels = new Set((cf.bsLinesRepresented || []).map(r => r.label));
    const missing = [];
    fullLineSet.forEach(key => {
      const label = key.split('::').slice(1).join('::');
      if (!represented.has(key) && !representedLabels.has(label)) missing.push(key);
    });
    ties.push({
      key: 'cf_lines_reconcile_to_bs',
      label: 'Every non-cash BS canonical line with a delta is represented in the Cash Flow',
      description: 'Every balance sheet line item that changed during the period (other than cash itself) must be explained on the Statement of Cash Flows — either as a working-capital adjustment, an investing activity, or a financing activity. A missing line means the cash impact of that account is unaccounted for.',
      ok: missing.length === 0,
      variance: missing.length,
      missingLines: missing,
    });

    // V15.1: cf_period_matches_is_period — the check that V15 missed.
    // V15.2: strengthened with (a) sampled WC line ties to BS deltas
    // (computed from period_start_balance, not prior_balance) and
    // (c) the internal-math identity: beginCash + ΣsectionSubtotals = endCash.
    // Item (c) would have caught the V15.1 -$481,258 plug.
    //
    // Legacy fallback: when the data lacks any V15.1 backend metadata
    // (no period_start_balance on any account AND no opts.cashAtPeriodStart),
    // we're in legacy/demo mode — emit the tie as "ok with caveat" so the
    // demo/test paths don't fail closed on the gate. Real engagements with
    // QBO connections always have the backend metadata and exercise the
    // full check.
    const inLegacyMode = (cf.hasPeriodStart !== true) && (opts.cashAtPeriodStart == null);
    const cfPeriodChecks = [];
    cfPeriodChecks.push({
      name: 'NI matches IS',
      ok: Math.abs(cf.netIncome - exactNI) < 1,
      detail: `cf=${cf.netIncome.toFixed(2)} is=${exactNI.toFixed(2)}`,
    });
    if (!inLegacyMode) {
      cfPeriodChecks.push({
        name: 'CF uses period_start balances (not 12-month prior)',
        ok: cf.hasPeriodStart === true,
        detail: cf.hasPeriodStart ? 'period_start_balance present' : 'FALLBACK to prior_balance (legacy)',
      });
    }
    if (opts.cashAtPeriodStart != null && Number.isFinite(opts.cashAtPeriodStart)) {
      // V19.10 — Surface the actual bank-account name mismatch when the
      // snapshot disagrees with the per-account roll-up.
      const pd = opts.qboMeta?.period_diagnostics || {};
      const unrec = pd.unrecoverable_zero_balance_banks || [];
      const entityNames = pd.entity_bank_names || [];
      const reportNames = pd.ps_eve_bank_names || [];
      const recoveries = pd.name_recoveries || [];
      const trace = pd.bank_resolve_trace || [];
      const matchesSnapshot = Math.abs(cf.beginCash - opts.cashAtPeriodStart) < 1;

      // V19.10.5 — Detect the most common cause of this failure:
      // a bank account whose backend `period_start_balance` is non-zero
      // but isn't mapped into the canonical Cash section.
      const cashLineAccountIds = new Set();
      const cashLineDump = [];  // V19.10.6 — collect what's actually in mapping.cash
      (mapping?.cash || []).forEach((item) => {
        const walk = (l, parentLabel) => {
          if (!l) return;
          if (l.type === 'group') {
            (l.children || []).forEach((c) => walk(c, l.label || parentLabel));
            return;
          }
          const ids = (l.accounts || []).map(String);
          ids.forEach((aid) => cashLineAccountIds.add(aid));
          cashLineDump.push({
            label: l.label || parentLabel || '(unnamed)',
            account_ids: ids,
          });
        };
        walk(item, null);
      });
      const unmappedBanks = trace.filter((t) =>
        Math.abs(t.period_start_balance || 0) > 0.5
        && !cashLineAccountIds.has(String(t.id)));

      let detail = `cf=${cf.beginCash.toFixed(2)} qbo=${opts.cashAtPeriodStart.toFixed(2)}`;
      if (!matchesSnapshot) {
        if (unmappedBanks.length > 0) {
          // The actionable case: bank accounts hold the right values
          // but they aren't in the Cash section of the canonical mapping.
          detail += `\n\n⚠️ Action required: ${unmappedBanks.length} bank account(s) hold period-start cash but the canonical mapping's Cash section doesn't include their account ID. Open Step 2 (Map Accounts) and drag the following into "Cash and Cash Equivalents":`;
          unmappedBanks.forEach((u) => {
            detail += `\n   • "${u.name}" (id=${u.id}) — period_start=$${(u.period_start_balance || 0).toFixed(2)}, current=$${(u.balance || 0).toFixed(2)}`;
          });
          // V19.10.6 — Dump what's CURRENTLY in mapping.cash so we can
          // tell whether the account is visually mapped but pointing at
          // a stale id. The user reported this happening after a QBO
          // reconnect — if mapping.cash contains an id that doesn't
          // exist in `accounts`, that's a stale-mapping bug.
          detail += `\n\nWhat the canonical Cash section currently points at:`;
          if (cashLineDump.length === 0) {
            detail += `\n   (empty — no lines in mapping.cash)`;
          } else {
            cashLineDump.forEach((cl) => {
              detail += `\n   • "${cl.label}" — account ids: [${cl.account_ids.join(', ') || '(none)'}]`;
            });
          }
          // Cross-check: do any mapped ids point at accounts that don't
          // exist in the current `accounts` array? That's stale.
          const allAccIds = new Set((accounts || []).map((a) => String(a.id)));
          const staleIds = [...cashLineAccountIds].filter((id) => !allAccIds.has(id));
          if (staleIds.length > 0) {
            detail += `\n\n⚠️ Stale account ids in Cash mapping (don't exist in current QBO sync): [${staleIds.join(', ')}]. This means the mapping was saved before QBO reissued account ids (after a reconnect). Open Step 2 and re-drag the bank account into Cash and Cash Equivalents.`;
          }
        } else {
          // Fall back to the deep-diagnostic display we had before, for
          // cases where the issue ISN'T a mapping omission.
          const recoveriesForBank = recoveries.filter((r) =>
            /bs_ps_eve|bs_cur/i.test(r.source || ''));
          detail += `\n• Entity-query bank names: [${entityNames.length ? entityNames.join(' | ') : '(none)'}]`;
          detail += `\n• QBO BS report bank names at period_start_eve: [${reportNames.length ? reportNames.join(' | ') : '(none)'}]`;
          detail += `\n• Name-fallback recoveries: ${recoveriesForBank.length}`;
          if (recoveriesForBank.length > 0) {
            detail += ` — recovered names: [${recoveriesForBank.map((r) => r.name).join(' | ')}]`;
          }
          detail += `\n• Unmatched zero-balance banks in entity query: ${unrec.length}`;
          if (unrec.length > 0) {
            detail += ` — names: [${unrec.map((u) => u.name).join(' | ')}]`;
          }
          if (trace.length > 0) {
            detail += `\n• Per-bank trace:`;
            trace.forEach((t) => {
              detail += `\n   - "${t.name}" (id=${t.id}): bal=${t.balance}, ps_eve=${t.period_start_balance}, mapped_to_cash=${cashLineAccountIds.has(String(t.id))}, id_in_ps_eve=${t.id_in_bs_ps_eve}, name_hit_ps_eve=${t.ps_eve_name_hit}, variants=[${(t.loose_norm_variants || []).join(',')}]`;
            });
          }
        }
      }
      cfPeriodChecks.push({
        name: 'Beginning cash matches QBO opening snapshot',
        ok: matchesSnapshot,
        detail,
      });
    }
    cfPeriodChecks.push({
      name: 'Ending cash matches BS cash',
      ok: Math.abs(cf.endCash - bsCashCurrent) < 1,
      detail: `cf=${cf.endCash.toFixed(2)} bs=${bsCashCurrent.toFixed(2)}`,
    });
    // V15.2 (c): internal-math identity. beginCash + Op + Inv + Fin
    // == endCash to the dollar. Catches the V15 plug-in-financing pattern.
    const internalIdentity = cf.beginCash
                           + cf.sections.operating.subtotal
                           + cf.sections.investing.subtotal
                           + cf.sections.financing.subtotal;
    cfPeriodChecks.push({
      name: 'CF internal math: begin + Op + Inv + Fin = end',
      ok: Math.abs(internalIdentity - cf.endCash) < 1,
      detail: `begin+ΣsubΣtotals=${internalIdentity.toFixed(2)} end=${cf.endCash.toFixed(2)} Δ=${(internalIdentity - cf.endCash).toFixed(2)}`,
    });
    // V15.2 (b): WC line sampling. For each of AR, AP, Deferred Revenue
    // (when present in the live data), confirm CF picks up the YTD delta
    // computed from period_start_balance, NOT from prior_balance.
    const findLineWithLabel = (sectionKey, namePattern) => {
      const items = mapping?.[sectionKey] || [];
      let found = null;
      const sign = SECTION_SIGN[sectionKey] ?? 1;
      const walk = (line) => {
        if (found || !line) return;
        if (line.type === 'group') { (line.children || []).forEach(walk); return; }
        const labelMatch = namePattern.test(line.label || '');
        const childMatch = (line.accounts || []).some(aid => {
          const a = accounts.find(x => String(x.id) === String(aid));
          return a && namePattern.test(a.name || '');
        });
        if (labelMatch || childMatch) {
          let cur = 0, start = 0;
          (line.accounts || []).forEach(aid => {
            const a = accounts.find(x => String(x.id) === String(aid));
            if (!a) return;
            cur   += sign * (a.balance || 0);
            start += sign * ((cf.hasPeriodStart ? a.period_start_balance : a.prior_balance) || 0);
          });
          found = { label: line.label, delta: cur - start };
        }
      };
      items.forEach(walk);
      return found;
    };
    const sampleLines = [
      { key: 'AR',  section: 'assets_current', pattern: /accounts\s+receivable|billed\s+a\/r|^a\/?r\b/i },
      { key: 'AP',  section: 'liabilities_current', pattern: /accounts\s+payable|^a\/?p\b/i },
      { key: 'DefRev', section: 'liabilities_current', pattern: /deferred\s+(revenue|income)|unearned/i },
    ];
    sampleLines.forEach(s => {
      const bsLine = findLineWithLabel(s.section, s.pattern);
      if (!bsLine || Math.abs(bsLine.delta) < 0.5) return;
      // Find the matching CF row (signed appropriately). For assets,
      // CF value = -delta; for liabilities, CF value = +delta.
      const cfRow = cf.sections.operating.rows.find(r => r.label && s.pattern.test(r.label));
      if (!cfRow) {
        // Could be rolled up — that's fine, this check is only when it's own-line.
        return;
      }
      const expectedCFValue = (s.section === 'assets_current' ? -1 : +1) * bsLine.delta;
      cfPeriodChecks.push({
        name: `WC sample: CF ${s.key} delta matches period_start-based BS delta`,
        ok: Math.abs(cfRow.value - expectedCFValue) < 1,
        detail: `cf=${cfRow.value.toFixed(2)} expected=${expectedCFValue.toFixed(2)} Δ=${(cfRow.value - expectedCFValue).toFixed(2)}`,
      });
    });
    const periodOk = cfPeriodChecks.every(c => c.ok);
    ties.push({
      key: 'cf_period_matches_is_period',
      label: 'Cash Flow inputs align with the period the statement claims to cover',
      description: 'The Statement of Cash Flows must be built from the same period boundaries (period start, period end, beginning cash, and YTD-comparable working-capital deltas) that the Income Statement and Balance Sheet are claiming to cover.',
      ok: periodOk,
      variance: periodOk ? 0 : cfPeriodChecks.filter(c => !c.ok).length,
      periodChecks: cfPeriodChecks,
    });
  }

  // ── V17 — Period-consistency tie family (BS PY, IS PY, T12) ──────────
  // Ports the V15.2 pattern (cf_period_matches_is_period) to the three
  // remaining prior-period / multi-period statements. Each check
  // verifies the rendered columns match what the engagement period
  // setting implies, so the published statement set tells one coherent
  // period story instead of mixing 4-month YTD with full-year prior.
  const qboMetaArg = opts.qboMeta;
  if (qboMetaArg && qboMetaArg.period_end) {
    const periodEnd = new Date(qboMetaArg.period_end + 'T12:00:00');
    const periodStart = qboMetaArg.period_start ? new Date(qboMetaArg.period_start + 'T12:00:00') : null;
    const monthsInPeriod = periodStart
      ? (periodEnd.getFullYear() - periodStart.getFullYear()) * 12 + (periodEnd.getMonth() - periodStart.getMonth()) + 1
      : null;

    // ── period_data_loaded (V25.20) ────────────────────────────────
    // Reported on MEET Ministry, custom period 2026-01-01..2026-07-31:
    // QuickBooks returned nothing for the Trial Balance, the Balance
    // Sheet AND the year-to-date P&L. Every one of those failures was
    // swallowed server-side, so the package rendered with zeros, ten
    // ties passed vacuously (0 = 0) and the only tie that noticed was
    // the T12 one — which then blamed the T12 for a missing Income
    // Statement. This tie is the front door: if the period's own data
    // did not load, nothing downstream is worth checking.
    const fetchReports = qboMetaArg.fetch_reports || null;
    const failedFetches = fetchReports
      ? Object.entries(fetchReports).filter(([, v]) => v && v.ok === false)
      : [];
    const describeFetch = ([name, v]) => describeFetchFailure(name, v);
    const dataChecks = [];
    dataChecks.push({
      name: "QuickBooks returned the period's profit & loss",
      ok: !!qboMetaArg.pl_period,
      detail: qboMetaArg.pl_period
        ? `P&L for ${qboMetaArg.period_start}..${qboMetaArg.period_end} loaded`
        : 'The Income Statement has NO year-to-date source for this period, so '
          + 'every figure on it is zero. Re-run the period from the dashboard; '
          + 'if it keeps failing, reconnect QuickBooks on the Import step.'
          + (fetchReports?.pl_period
             ? ` QuickBooks said: ${describeFetch(['pl_period', fetchReports.pl_period])}`
             : ''),
    });
    dataChecks.push({
      name: 'every QuickBooks report pull for this period succeeded',
      ok: failedFetches.length === 0,
      detail: failedFetches.length === 0
        ? (fetchReports ? 'all report pulls returned 200' : 'no fetch log on this payload (older sync — re-run the period)')
        : failedFetches.map(describeFetch).join('  ·  '),
    });
    const periodDataOk = dataChecks.every((c) => c.ok);
    ties.push({
      key: 'period_data_loaded',
      label: "QuickBooks returned this period's data (no silent empty pulls)",
      description: "Every statement in the package is built from the period's Trial Balance, Balance Sheet and profit & loss. When one of those pulls comes back empty the affected statement renders as zeros — and a page of zeros satisfies most arithmetic ties by accident. This tie fails the package on the pull itself, before any figure is judged.",
      ok: periodDataOk,
      variance: periodDataOk ? 0 : dataChecks.filter((c) => !c.ok).length,
      periodChecks: dataChecks,
      diagnostic: fetchReports || undefined,
    });

    // ── bs_py_period_matches_engagement ────────────────────────────
    // Verifies the prior-period column on the Balance Sheet uses the
    // basis declared in client.bs_py_comparison_basis. We can't easily
    // compare *rendered* dates from inside runValidationTies (the
    // dates live in PrintModal), but we CAN sanity-check the
    // prior_balance vs period_start_balance fields the BS PY will use.
    const bsPyBasis = opts.bsPyComparisonBasis || 'prior_year_same_date';
    let bsPyOk = true;
    let bsPyDetail = `basis=${bsPyBasis}`;
    // Defense-in-depth: if the engagement says period_start_basis, the
    // backend MUST have populated period_start_balance on accounts.
    if (bsPyBasis === 'period_start_basis') {
      const anyPeriodStart = (accounts || []).some(
        (a) => a.period_start_balance !== null && a.period_start_balance !== undefined
      );
      if (!anyPeriodStart) {
        bsPyOk = false;
        bsPyDetail += ' but no period_start_balance found on any account';
      }
    } else {
      // prior_year_same_date — every account should have a prior_balance.
      const anyPrior = (accounts || []).some(
        (a) => a.prior_balance !== null && a.prior_balance !== undefined
      );
      if (!anyPrior) {
        bsPyOk = false;
        bsPyDetail += ' but no prior_balance found on any account';
      }
    }
    ties.push({
      key: 'bs_py_period_matches_engagement',
      label: 'Balance Sheet PY comparison uses the configured basis',
      description: bsPyBasis === 'period_start_basis'
        ? 'The Balance Sheet — Prior Year Comparison must use period-start balances (e.g. 12/31/2025 for a 4/30/2026 interim package) when the engagement comparison basis is set to period_start_basis.'
        : 'The Balance Sheet — Prior Year Comparison must use prior-year same-date balances (e.g. 4/30/2025 for a 4/30/2026 period end) when the engagement comparison basis is set to prior_year_same_date.',
      ok: bsPyOk,
      variance: bsPyOk ? 0 : 1,
      detail: bsPyDetail,
    });

    // ── is_py_period_matches_engagement ────────────────────────────
    // The IS PY column for the prior year must cover the SAME period
    // length as the current year. SMJ interim 4-month YTD must compare
    // to 4 months ended 4/30/2025, not Year Ended 12/31/2025. We
    // approximate this by checking that the prior_balance accumulators
    // on IS accounts are populated (so the column has data) AND that
    // the engagement has a defined period_start (so the period length
    // is well-defined).
    //
    // V19.8 — First-period engagement carve-out. For a freshly-onboarded
    // QBO realm whose books literally don't extend into the prior-year
    // comparison window, the PY column will (legitimately) be empty.
    // That's not a publish-blocking math error — it's a "no comparison
    // available" data state. Detection: BOTH prior_balance AND
    // period_start_balance roll up to zero across every BS account
    // (asset/liability/equity). When that's true we mark the tie as
    // INFORMATIONAL (ok=true, but carry a non-blocking note) so the
    // publish gate doesn't stick on a brand-new client.
    const totalPriorIS = ['income', 'cogs', 'opex', 'other_income', 'other_expense']
      .reduce((s, sec) => s + Math.abs(sectionTotal(accounts, mapping, sec, 'prior')), 0);
    // V19.8 — Also count UNMAPPED prior IS activity. When the backend
    // synthesizes deleted IS accounts from the prior-period TB (because
    // QBO deleted/renumbered them between periods), those accounts land
    // in `accounts` but aren't yet attached to any canonical mapping
    // line. We want the tie to pass as soon as the data is there, not
    // wait for the user to manually map every synthesized leaf.
    const totalRawPriorIS = (accounts || [])
      .filter((a) => ['Income', 'Expense', 'COGS'].includes(a.type)
        || /^(Income|Expense|Cost of Goods Sold)$/i.test(a.qbo_type || ''))
      .reduce((s, a) => s + Math.abs(a.prior_balance || 0), 0);
    const totalPriorISAny = Math.max(totalPriorIS, totalRawPriorIS);
    const totalPriorBS = (accounts || []).reduce(
      (s, a) => s + Math.abs(a.prior_balance || 0), 0
    );
    const totalPSEveBS = (accounts || []).reduce(
      (s, a) => s + Math.abs(a.period_start_balance || 0), 0
    );
    const isFirstPeriodEngagement = totalPriorISAny < 1
      && totalPriorBS < 1
      && totalPSEveBS < 1
      && !!periodStart;

    let isPyOk = true;
    const isPyChecks = [];
    isPyChecks.push({
      name: 'engagement has explicit period_start (so PY length is known)',
      ok: !!periodStart,
      detail: periodStart
        ? `period_start=${qboMetaArg.period_start}`
        : 'Open Engagement Settings → set Period Start / Period End for this client',
    });
    isPyChecks.push({
      name: 'IS prior column has non-zero data (prior_balance populated)',
      ok: totalPriorISAny > 0 || isFirstPeriodEngagement,
      detail: totalPriorISAny > 0
        ? (totalPriorIS > 0
            ? `Σ|prior IS| = $${totalPriorIS.toFixed(0)}`
            : `Σ|raw prior IS| = $${totalRawPriorIS.toFixed(0)} (some accounts unmapped — open Step 2 to attach them to canonical lines)`)
        : (isFirstPeriodEngagement
            ? 'First-period engagement detected — QBO has no activity in the prior-year comparison window. PY column will be hidden on the deliverable.'
            : (() => {
                const pd = qboMetaArg.period_diagnostics || {};
                const range = `${pd.prior_period_start_used || '?'}..${pd.prior_period_end_used || '?'}`;
                return `QBO /TrialBalance for ${range} returned ${pd.prior_tb_rows ?? '?'} rows totaling $${(pd.prior_tb_total_abs ?? 0).toFixed(0)} — and BS leaves at same range returned ${pd.bs_leaves_prior_rows ?? '?'} rows. If you can see this period in QBO's own reports but rows=0 here, the realm token may need to be reconnected.`;
              })()),
    });
    isPyOk = isPyChecks.every((c) => c.ok);
    ties.push({
      key: 'is_py_period_matches_engagement',
      label: 'IS PY prior-year column covers the same period length as the current year',
      description: 'For an interim YTD package (e.g. four months ended April 30, 2026), the prior-year column must cover the same four-month YTD period in the prior year, not a full year or trailing-twelve. Different period lengths produce non-comparable variance %.',
      ok: isPyOk,
      variance: isPyOk ? 0 : isPyChecks.filter((c) => !c.ok).length,
      periodChecks: isPyChecks,
      firstPeriodEngagement: isFirstPeriodEngagement,
    });

    // ── t12_period_matches_engagement ──────────────────────────────
    // T12 must cover exactly the 12 months ending on engagement
    // period_end. Verified by checking ttmData has 12 buckets AND
    // the last bucket's period_end matches engagement period_end
    // (year + month).
    const t12Months = Array.isArray(ttmData?.months) ? ttmData.months : null;
    let t12Ok = true;
    const t12Checks = [];
    if (!t12Months) {
      t12Checks.push({
        name: 'Trailing 12 Months data loaded from QuickBooks',
        ok: false,
        detail: 'The /api/qbo/ttm pull returned nothing, so the T12 statement '
          + 'has no monthly columns to verify. Retry the pull from the banner '
          + 'above; if QuickBooks keeps refusing, reconnect it on the Import step.',
      });
      t12Ok = false;
    } else {
      t12Checks.push({ name: 'T12 has exactly 12 monthly buckets', ok: t12Months.length === 12 });
      if (t12Months.length > 0) {
        const last = t12Months[t12Months.length - 1];
        const lastEnd = last?.month_end || last?.end || null;
        if (lastEnd) {
          const le = new Date(lastEnd + 'T12:00:00');
          const sameMonth =
            le.getFullYear() === periodEnd.getFullYear() &&
            le.getMonth() === periodEnd.getMonth();
          t12Checks.push({ name: 'last T12 month_end matches engagement period_end', ok: sameMonth });
        } else {
          t12Checks.push({ name: 'T12 month_end metadata present', ok: false });
        }
      }
      t12Ok = t12Checks.every((c) => c.ok);
    }
    ties.push({
      key: 't12_period_matches_engagement',
      label: 'Trailing 12 Months ends on engagement period_end with 12 monthly columns',
      description: 'The T12 statement must show exactly 12 monthly columns ending on the engagement period_end. Off-by-one month boundaries break the trailing-twelve narrative and de-sync from the IS YTD figures.',
      ok: t12Ok,
      variance: t12Ok ? 0 : t12Checks.filter((c) => !c.ok).length,
      periodChecks: t12Checks,
    });

    // ── t12_ni_ties_to_is_ytd (V18.1, 2026-06-10) ──────────────────────
    // Sum of T12 monthly NI for months falling within
    // engagement.period_start..period_end MUST equal IS YTD NI to the
    // dollar — on UNROUNDED backend values. Under the V18.1 "one
    // source, two presentations" architecture, the tie holds by
    // construction: the backend `_fetch_qbo_ttm` emits the same
    // V22.2-parsed detail_lines per month as `_fetch_qbo_pl_period`
    // does YTD, and `buildT12` runs `buildIncomeStatementV22` per
    // month inheriting V18 cross-section contra reclass. Any
    // divergence means the data paths forked again — render must
    // block. Three-state policy: tied or blocked, never plugged.
    //
    // Comparison is unrounded; any |Δ| ≥ $0.01 of float-equal NI is a
    // failure (no $1 tolerance — that's the wrong layer to compare).
    let t12NiTie = { ok: true, variance: 0, computed: null };
    if (ttmData?.detail_lines_monthly && Array.isArray(ttmData?.months) && periodStart && periodEnd) {
      // Build the T12 using the same accounts+mapping the IS uses.
      const monthLabels = ttmData.months.map((m) => `${m.month} ${String(m.year).slice(-2)}`);
      const t12 = buildT12(accounts, mapping, ttmData, monthLabels);
      let sumInPeriod = 0;
      let monthsInPeriod = 0;
      const monthlyNi = (t12._v22Monthly || []).map((m) => Number(m.netIncome) || 0);
      const monthsMeta = ttmData.months || [];
      for (let i = 0; i < monthsMeta.length; i++) {
        const mStart = monthsMeta[i].start ? new Date(monthsMeta[i].start + 'T12:00:00') : null;
        const mEnd   = monthsMeta[i].end   ? new Date(monthsMeta[i].end   + 'T12:00:00') : null;
        if (!mStart || !mEnd) continue;
        if (mStart >= periodStart && mEnd <= periodEnd) {
          sumInPeriod += (monthlyNi[i] || 0);
          monthsInPeriod++;
        }
      }
      const isYtdNI = Number(is?._v22?.exactNetIncome) || Number(is?.totals?.netIncome) || 0;
      const delta = sumInPeriod - isYtdNI;
      const ok = Math.abs(delta) < 0.01;
      t12NiTie = {
        ok,
        variance: delta,
        // V25.20 — when the Income Statement has no source for the period,
        // the variance is not evidence that the T12 forked from the IS: it is
        // the whole T12 measured against an empty statement. Say so, and
        // point at the tie that owns the real failure.
        missingIsSource: !qboMetaArg.pl_period,
        computed: {
          sum_t12_monthly_ni: Number(sumInPeriod.toFixed(2)),
          is_ytd_ni:          Number(isYtdNI.toFixed(2)),
          months_in_period:   monthsInPeriod,
          delta:              Number(delta.toFixed(4)),
          unrounded:          true,
          ...(qboMetaArg.pl_period ? {} : {
            cause: "QuickBooks returned no profit & loss for this period, so "
              + "the Income Statement is all zeros and this variance is simply "
              + "the T12 total. See the \"QuickBooks returned this period's "
              + "data\" tie.",
          }),
        },
      };
    } else {
      t12NiTie = { ok: true, variance: 0, computed: { skipped: 'detail_lines_monthly or engagement period missing' } };
    }
    ties.push({
      key: 't12_ni_ties_to_is_ytd',
      label: t12NiTie.missingIsSource
        ? 'T12 monthly Net Income sums to IS YTD Net Income — the Income Statement has no data for this period'
        : 'T12 monthly Net Income sums to IS YTD Net Income (one source, two presentations)',
      description: t12NiTie.missingIsSource
        ? "QuickBooks returned no profit & loss for this period, so the Income Statement is all zeros and this variance is simply the T12 total measured against nothing. The failure to fix is the one reported by \"QuickBooks returned this period's data\" — re-run the period, and reconnect QuickBooks on the Import step if it keeps coming back empty."
        : 'The Trailing-12-Months statement is built from the same canonical-mapped, V18 reclassification-adjusted data as the Income Statement. The sum of T12 monthly NI for months falling within the engagement period MUST equal the IS YTD NI to the penny on unrounded backend values. A non-zero variance means the T12 data source forked from the IS data source (e.g., a parser patch went into one but not the other). Rendering is BLOCKED on failure — the three-state policy is tied or blocked, never plugged.',
      ok: t12NiTie.ok,
      variance: t12NiTie.variance,
      computed: t12NiTie.computed,
    });
  }

  // ── V17 — framework_consistency_check tie ────────────────────────────
  // Under GAAP, owner distributions (Partner Pay, Owner Draw, Member
  // Distribution) are equity transactions, not P&L items. If the
  // engagement framework is GAAP AND any canonical P&L line is flagged
  // as is_owner_distribution_in_pl=true (or matches the heuristic name
  // pattern when the flag is undefined), the framework consistency
  // tie reports red with a remediation message.
  //
  // V18 — when framework='none' (AR-C 70 no-framework path), bypass
  // this check entirely. There is no framework claim to be inconsistent
  // with.
  // V19 — additionally skip on PREPARED engagements regardless of the
  // stored framework value. Per V19 spec, prepared engagements always
  // render the AR-C 70.A6 disclosure (no framework claimed), so there
  // is no framework consistency to verify. The tie remains active on
  // COMPILATION engagements where the framework IS claimed in the
  // report wording.
  const frameworkOpt = (opts.framework || 'gaap').toLowerCase();
  const engagementTypeOpt = (opts.engagementType || 'preparation').toLowerCase();
  if (frameworkOpt === 'gaap' && engagementTypeOpt === 'compilation') {
    const OWNER_DIST_HEURISTIC = /partner\s*(pay|draw|distribution)|owner\s*(draw|distribution)|member.*(draw|distribution)/i;
    const flaggedLines = [];
    const isOwnerDistLine = (line) => {
      if (line.is_owner_distribution_in_pl === true) return true;
      if (line.is_owner_distribution_in_pl === false) return false;
      // Undefined → heuristic on the label string.
      return OWNER_DIST_HEURISTIC.test(line.label || '');
    };
    const walkLine = (sec, line) => {
      if (!line) return;
      if (line.type === 'group') {
        if (isOwnerDistLine(line)) {
          flaggedLines.push(`${sec}::${line.label}`);
        }
        (line.children || []).forEach((c) => walkLine(sec, c));
        return;
      }
      if (isOwnerDistLine(line)) {
        flaggedLines.push(`${sec}::${line.label}`);
      }
    };
    ['income', 'cogs', 'opex', 'other_income', 'other_expense'].forEach((sec) => {
      (mapping?.[sec] || []).forEach((l) => walkLine(sec, l));
    });
    const frameworkOk = flaggedLines.length === 0;
    ties.push({
      key: 'framework_consistency_check',
      label: 'Owner / partner distributions are NOT presented in the P&L under GAAP',
      description: frameworkOk
        ? 'Engagement framework is GAAP; no canonical P&L lines are flagged as owner distributions.'
        : `Framework is GAAP but ${flaggedLines.length} P&L line${flaggedLines.length === 1 ? ' is' : 's are'} flagged as an owner distribution (${flaggedLines.slice(0, 3).join(', ')}${flaggedLines.length > 3 ? ', …' : ''}). Under GAAP, partner / member distributions are equity transactions and must be presented in the equity section of the Balance Sheet, not on the Income Statement. Either (a) change engagement framework to Income Tax Basis or Modified Cash, or (b) reclassify these accounts to the Distributions / Owner's Equity equity line.`,
      ok: frameworkOk,
      variance: flaggedLines.length,
      flaggedLines,
    });
  }

  // Draft flags — Statement-level gate. CF lifts its draft automatically when
  // the two CF ties pass; staff don't have to flip a config flag.
  const cashFlowDraft    = !cashFlowTiesPassing && opts.cashFlowCanonical !== true;
  const bsPriorYearDraft = opts.bsPriorYearCanonical !== true;
  const isPriorYearDraft = opts.isPriorYearCanonical !== true;
  const t12Draft         = opts.t12Canonical !== true;

  const drafts = [];
  if (cashFlowDraft)    drafts.push({ key: 'cf',    label: 'Statement of Cash Flows' });
  if (bsPriorYearDraft) drafts.push({ key: 'bs_py', label: 'Balance Sheet — Prior Year Comparison' });
  if (isPriorYearDraft) drafts.push({ key: 'is_py', label: 'Statement of Income — Prior Year Comparison' });
  if (t12Draft)         drafts.push({ key: 't12',   label: 'Trailing 12 Months Statement of Income' });

  void ttmData;

  return {
    ties,
    allGreen: ties.every(t => t.ok),
    drafts,
    cashFlowDraft,
    bsPriorYearDraft,
    isPriorYearDraft,
    t12Draft,
    publishable: ties.every(t => t.ok) && drafts.length === 0,
  };
}

// Engagement type → SSARS report mapping. Drives the cover-page accountant's
// report attached to every published snapshot and the per-page footer wording.
//
// SOURCE REFERENCE (2026-05-21): the verbatim wording flowed into the
// compilation report template (see `COMPILATION_REPORT_TEMPLATE` and
// `PREPARED_FOOTER_TEMPLATE` below, and renderCompilationReport in
// PrintModal.js) is built against AICPA AR-C Section 80 and AR-C Section
// 70 as illustrated in SSARS No. 25 (effective for periods ending on or
// after December 15, 2021). When AICPA publishes a SSARS update, the
// strings here are the only place to change. Track via the AICPA's
// "Statements on Standards for Accounting and Review Services"
// publication and your firm's PPC Guide subscription.
//   Reference: AICPA AR-C §80 — Compilation Engagements (illustrative
//              reports in §80.A39 — §80.A47).
//   Reference: AICPA AR-C §70 — Preparation of Financial Statements
//              (§70.15 disclaimer requirement, §70.A22 — §70.A24).
//   Built against: SSARS 25.
// ═══════════════════════════════════════════════════
//   V20 — Company Consolidations (Simple Roll-Up)
// ═══════════════════════════════════════════════════
//
// Strategy: build each member's canonical statements with the existing
// per-client builders (`buildIncomeStatement`, `buildBalanceSheet`,
// `buildCashFlow`, `buildT12`), then merge the structured outputs by
// canonical line LABEL. No intercompany eliminations in v1 (Fathom-style
// simple roll-up). Identity guarantees:
//   • Each member's CF identity is enforced per builder (Rule 4) at the
//     member level, so the consolidated CF identity is the SUM of member
//     identities — also exactly $0.00 by construction.
//   • Consolidated BS balances (A = L + E) by the same argument.
//   • Consolidated IS NI = sum of member NIs.
//
// Period coherence: callers must pass members with the same reporting
// period_end. The /api/consolidated endpoint resolves this server-side.
//
// Returns the SAME shape as the per-member builders so renderers consume
// either with one code path.

const _normLabel = (s) => (s || '').trim().toLowerCase();

function _mergeLeafList(leavesPerMember) {
  // leavesPerMember: array of arrays. Each inner array is a member's list of
  // {label, current, prior, accounts} (or {months,total} for T12 leaves).
  // Result: merged list, summed by normalized label; order = first-occurrence.
  const order = [];
  const byKey = new Map();
  leavesPerMember.forEach((memberLeaves) => {
    (memberLeaves || []).forEach((leaf) => {
      const key = _normLabel(leaf.label);
      if (!byKey.has(key)) {
        byKey.set(key, {
          label: leaf.label,
          current: 0,
          prior: 0,
          accounts: [],
          ...(leaf.months !== undefined ? { months: Array((leaf.months || []).length).fill(0), total: 0 } : {}),
        });
        order.push(key);
      }
      const acc = byKey.get(key);
      acc.current += leaf.current || 0;
      acc.prior += leaf.prior || 0;
      if (leaf.accounts) acc.accounts = acc.accounts.concat(leaf.accounts);
      if (leaf.months) {
        leaf.months.forEach((v, i) => { acc.months[i] = (acc.months[i] || 0) + (v || 0); });
        acc.total = (acc.total || 0) + (leaf.total || 0);
      }
    });
  });
  return order.map((k) => byKey.get(k));
}

function _mergeItems(itemsPerMember) {
  // Merge structured BS/IS items by label. Supports both 'leaf' and 'group' kinds.
  // For groups, merge by group label, then merge their leaves by label.
  const order = [];
  const byKey = new Map();
  itemsPerMember.forEach((memberItems) => {
    (memberItems || []).forEach((it) => {
      const key = (it.kind || 'leaf') + '::' + _normLabel(it.label);
      if (!byKey.has(key)) {
        if (it.kind === 'group') {
          byKey.set(key, {
            kind: 'group', label: it.label,
            _leavesPerMember: [it.leaves || []],
            subtotal: 0, subtotal_prior: 0,
          });
        } else {
          byKey.set(key, {
            kind: 'leaf', label: it.label,
            current: it.current || 0, prior: it.prior || 0,
            accounts: it.accounts ? [...it.accounts] : [],
          });
        }
        order.push(key);
      } else {
        const entry = byKey.get(key);
        if (entry.kind === 'group') {
          entry._leavesPerMember.push(it.leaves || []);
        } else {
          entry.current += it.current || 0;
          entry.prior += it.prior || 0;
          if (it.accounts) entry.accounts = entry.accounts.concat(it.accounts);
        }
      }
    });
  });
  return order.map((k) => {
    const entry = byKey.get(k);
    if (entry.kind === 'group') {
      const leaves = _mergeLeafList(entry._leavesPerMember);
      return {
        kind: 'group',
        label: entry.label,
        leaves,
        subtotal: leaves.reduce((s, l) => s + (l.current || 0), 0),
        subtotal_prior: leaves.reduce((s, l) => s + (l.prior || 0), 0),
      };
    }
    return entry;
  });
}

function _mergeISSection(memberSections) {
  // memberSections: array of {key, lines, items, total, total_prior, exact, exact_prior}
  const items = _mergeItems(memberSections.map((s) => s?.items || []));
  const lines = _mergeLeafList(memberSections.map((s) => s?.lines || []));
  return {
    key: memberSections[0]?.key,
    lines,
    items,
    total: memberSections.reduce((s, m) => s + (m?.total || 0), 0),
    total_prior: memberSections.reduce((s, m) => s + (m?.total_prior || 0), 0),
    exact: memberSections.reduce((s, m) => s + (m?.exact || 0), 0),
    exact_prior: memberSections.reduce((s, m) => s + (m?.exact_prior || 0), 0),
  };
}

export function mergeIncomeStatements(memberIS) {
  // memberIS: array of buildIncomeStatement() outputs.
  if (!memberIS || memberIS.length === 0) return null;
  const sectionKeys = ['income', 'cogs', 'opex', 'otherIncome', 'otherExpense'];
  const mergedSections = {};
  sectionKeys.forEach((sk) => {
    mergedSections[sk] = _mergeISSection(memberIS.map((m) => m?.sections?.[sk] || {}));
  });
  const sum = (path) => memberIS.reduce((s, m) => s + (m?.[path[0]]?.[path[1]] || 0), 0);
  return {
    sections: mergedSections,
    totals: {
      totalRevenue: sum(['totals', 'totalRevenue']),
      totalCOGS:    sum(['totals', 'totalCOGS']),
      grossProfit:  sum(['totals', 'grossProfit']),
      totalOpEx:    sum(['totals', 'totalOpEx']),
      ebit:         sum(['totals', 'ebit']),
      otherIncome:  sum(['totals', 'otherIncome']),
      otherExpense: sum(['totals', 'otherExpense']),
      netIncome:    sum(['totals', 'netIncome']),
    },
    prior: {
      totalRevenue: sum(['prior', 'totalRevenue']),
      totalCOGS:    sum(['prior', 'totalCOGS']),
      grossProfit:  sum(['prior', 'grossProfit']),
      totalOpEx:    sum(['prior', 'totalOpEx']),
      ebit:         sum(['prior', 'ebit']),
      otherIncome:  sum(['prior', 'otherIncome']),
      otherExpense: sum(['prior', 'otherExpense']),
      netIncome:    sum(['prior', 'netIncome']),
    },
  };
}

function _mergeBSSection(memberSections) {
  const items = _mergeItems(memberSections.map((s) => s?.items || []));
  const lines = _mergeLeafList(memberSections.map((s) => s?.lines || []));
  return {
    key: memberSections[0]?.key,
    lines,
    items,
    total:       memberSections.reduce((s, m) => s + (m?.total || 0), 0),
    total_prior: memberSections.reduce((s, m) => s + (m?.total_prior || 0), 0),
    exact:       memberSections.reduce((s, m) => s + (m?.exact || 0), 0),
    exact_prior: memberSections.reduce((s, m) => s + (m?.exact_prior || 0), 0),
  };
}

export function mergeBalanceSheets(memberBS) {
  if (!memberBS || memberBS.length === 0) return null;
  const sectionKeys = ['cash', 'assetsCur', 'assetsFix', 'assetsOther',
                       'liabCur', 'debtShort', 'liabLong', 'equity'];
  const mergedSections = {};
  sectionKeys.forEach((sk) => {
    mergedSections[sk] = _mergeBSSection(memberBS.map((m) => m?.sections?.[sk] || {}));
  });
  const sum = (path) => memberBS.reduce((s, m) => s + (m?.[path[0]]?.[path[1]] || 0), 0);
  return {
    sections: mergedSections,
    totals: {
      totalAssets:         sum(['totals', 'totalAssets']),
      totalLiab:           sum(['totals', 'totalLiab']),
      totalEquity:         sum(['totals', 'totalEquity']),
      totalEquityBeforeNI: sum(['totals', 'totalEquityBeforeNI']),
      totalLE:             sum(['totals', 'totalLE']),
      netIncome:           sum(['totals', 'netIncome']),
    },
    prior: {
      totalAssets:         sum(['prior', 'totalAssets']),
      totalLiab:           sum(['prior', 'totalLiab']),
      totalEquity:         sum(['prior', 'totalEquity']),
      totalEquityBeforeNI: sum(['prior', 'totalEquityBeforeNI']),
      totalLE:             sum(['prior', 'totalLE']),
      netIncome:           sum(['prior', 'netIncome']),
    },
    exact: {
      totalAssets:              sum(['exact', 'totalAssets']),
      totalLiab:                sum(['exact', 'totalLiab']),
      totalEquityBeforeNI:      sum(['exact', 'totalEquityBeforeNI']),
      totalAssetsPrior:         sum(['exact', 'totalAssetsPrior']),
      totalLiabPrior:           sum(['exact', 'totalLiabPrior']),
      totalEquityPriorBeforeNI: sum(['exact', 'totalEquityPriorBeforeNI']),
    },
  };
}

function _mergeCFRowList(rowsPerMember) {
  // CF rows: {label, value, indent?, subheader?, rollup?, isCarveOut?, kind?}
  // Subheader/structural rows (e.g. "Adjustments to reconcile...") have no
  // value; preserve the first member's structural rows in order.
  const order = [];
  const byKey = new Map();
  rowsPerMember.forEach((memberRows) => {
    (memberRows || []).forEach((r) => {
      if (r.subheader) {
        const k = 'sub::' + _normLabel(r.label);
        if (!byKey.has(k)) {
          byKey.set(k, { ...r });
          order.push(k);
        }
        return;
      }
      const k = (r.rollup ? 'roll::' : 'leaf::') + _normLabel(r.label);
      if (!byKey.has(k)) {
        byKey.set(k, { ...r, value: 0 });
        order.push(k);
      }
      byKey.get(k).value = (byKey.get(k).value || 0) + (r.value || 0);
    });
  });
  return order.map((k) => byKey.get(k));
}

export function mergeCashFlows(memberCF) {
  if (!memberCF || memberCF.length === 0) return null;
  const sectionKeys = ['operating', 'investing', 'financing', 'other'];
  const sum = (path) => memberCF.reduce((s, m) => s + (m?.[path] || 0), 0);
  const mergedSections = {};
  sectionKeys.forEach((sk) => {
    const memberSecs = memberCF.map((m) => m?.sections?.[sk]).filter(Boolean);
    if (memberSecs.length === 0) {
      mergedSections[sk] = sk === 'other' ? null : { rows: [], subtotal: 0 };
      return;
    }
    const rows = _mergeCFRowList(memberSecs.map((s) => s?.rows || []));
    const subtotal = memberSecs.reduce((s, m) => s + (m?.subtotal || 0), 0);
    mergedSections[sk] = sk === 'other' && rows.length === 0
      ? null
      : { rows, subtotal };
  });
  return {
    hasPeriodStart: memberCF.some((m) => m?.hasPeriodStart),
    hasPrior:       memberCF.some((m) => m?.hasPrior),
    netIncome:      sum('netIncome'),
    dAndA:          sum('dAndA'),
    threshold:      Math.max(...memberCF.map((m) => m?.threshold || 0), 0),
    thresholdAutoComputed: memberCF.every((m) => m?.thresholdAutoComputed !== false),
    niAtPeriodStart: sum('niAtPeriodStart'),
    sections: mergedSections,
    supplemental: {
      interestPaid: memberCF.reduce((s, m) => s + (m?.supplemental?.interestPaid || 0), 0),
      taxesPaid:    memberCF.reduce((s, m) => s + (m?.supplemental?.taxesPaid    || 0), 0),
    },
    netChange:           sum('netChange'),
    beginCash:           sum('beginCash'),
    endCash:             sum('endCash'),
    bsLinesRepresented:  memberCF.reduce((s, m) => s + ((m?.bsLinesRepresented || []).length || 0), 0),
    bsAccountsRepresented: memberCF.reduce((acc, m) => acc.concat(m?.bsAccountsRepresented || []), []),
    nonCashBSDeltaTotal: sum('nonCashBSDeltaTotal'),
    unclassifiedAccountAdvisories: memberCF.reduce((acc, m) => acc.concat(m?.unclassifiedAccountAdvisories || []), []),
  };
}

export function mergeT12s(memberT12, ttmMonthLabels = []) {
  if (!memberT12 || memberT12.length === 0) return null;
  const n = ttmMonthLabels.length
    || memberT12[0]?.months?.length
    || 12;
  const months = ttmMonthLabels.length > 0
    ? ttmMonthLabels
    : (memberT12[0]?.months || Array.from({ length: n }, (_, i) => `M${i+1}`));

  const mergeSection = (sectionLabel) => {
    const memberSections = memberT12.map(
      (t) => (t?.sections || []).find((s) => _normLabel(s.label) === _normLabel(sectionLabel))
    ).filter(Boolean);
    if (memberSections.length === 0) return null;
    const isSubtotal = memberSections[0].isSubtotal;
    const isMargin = memberSections[0].isMargin;
    const sign = memberSections[0].sign;
    if (isMargin) {
      // Margin recomputed after Net Income is known — return placeholder.
      return { label: sectionLabel, isMargin: true,
               monthsTotal: Array(n).fill(null), total: null };
    }
    // Merge rows by label
    const rowsPerMember = memberSections.map((s) => s.rows || []);
    // Need to handle group_header, group_subtotal, leaf — keep structure
    // by source order; merge values by label.
    const order = [];
    const byKey = new Map();
    rowsPerMember.forEach((memberRows) => {
      memberRows.forEach((r) => {
        const k = (r.kind || 'leaf') + '::' + _normLabel(r.label);
        if (!byKey.has(k)) {
          if (r.kind === 'group_header') {
            byKey.set(k, { kind: 'group_header', label: r.label });
          } else {
            byKey.set(k, {
              kind: r.kind || 'leaf', label: r.label,
              months: Array(n).fill(0), total: 0,
            });
          }
          order.push(k);
        }
        const entry = byKey.get(k);
        if (entry.kind === 'group_header') return;
        (r.months || []).forEach((v, i) => { entry.months[i] += (v || 0); });
        entry.total += (r.total || 0);
      });
    });
    const rows = order.map((k) => byKey.get(k));
    const monthsTotal = Array(n).fill(0);
    rows.forEach((r) => {
      if (r.kind === 'group_header' || r.kind === 'group_subtotal') return;
      (r.months || []).forEach((v, i) => { monthsTotal[i] += (v || 0); });
    });
    const total = monthsTotal.reduce((s, v) => s + v, 0);
    const out = { label: sectionLabel, monthsTotal, total };
    if (isSubtotal) {
      out.isSubtotal = true;
      // For pure subtotal rows (Gross Profit, Net Income), sum the member subtotals
      const memberSubtotalsMonths = memberSections.map((s) => s.monthsTotal || []);
      const subMonths = Array(n).fill(0);
      memberSubtotalsMonths.forEach((mm) => mm.forEach((v, i) => { subMonths[i] += (v || 0); }));
      out.monthsTotal = subMonths;
      out.total = subMonths.reduce((s, v) => s + v, 0);
    } else {
      out.rows = rows;
    }
    if (sign) out.sign = sign;
    return out;
  };

  const sectionOrder = ['Revenue', 'Cost of Goods Sold', 'Gross Profit',
                        'Operating Expenses', 'Other Income', 'Other Expense',
                        'Net Income', 'Net Margin'];
  const sections = sectionOrder.map(mergeSection).filter(Boolean);

  // Recompute Net Margin row from consolidated NI / Revenue
  const revenueSec = sections.find((s) => _normLabel(s.label) === 'revenue');
  const niSec = sections.find((s) => _normLabel(s.label) === 'net income');
  const marginIdx = sections.findIndex((s) => s.isMargin);
  if (marginIdx >= 0 && revenueSec && niSec) {
    const marginMonths = revenueSec.monthsTotal.map((rev, i) =>
      Math.abs(rev) < 0.005 ? null : (niSec.monthsTotal[i] / rev) * 100);
    const marginTotal = Math.abs(revenueSec.total) < 0.005
      ? null : (niSec.total / revenueSec.total) * 100;
    sections[marginIdx].monthsTotal = marginMonths;
    sections[marginIdx].total = marginTotal;
  }

  return {
    months,
    sections,
    totals: {
      revenue:       revenueSec?.total || 0,
      cogs:          sections.find((s) => _normLabel(s.label) === 'cost of goods sold')?.total || 0,
      gross_profit:  sections.find((s) => _normLabel(s.label) === 'gross profit')?.total || 0,
      opex:          sections.find((s) => _normLabel(s.label) === 'operating expenses')?.total || 0,
      other_income:  sections.find((s) => _normLabel(s.label) === 'other income')?.total || 0,
      other_expense: sections.find((s) => _normLabel(s.label) === 'other expense')?.total || 0,
      net_income:    niSec?.total || 0,
      net_margin:    sections[marginIdx]?.total ?? null,
    },
  };
}

// Top-level helper: takes an array of {accounts, mapping, ttm, qboMeta}
// member payloads, runs each member's builders, then merges. Returns an
// object identical in shape to a single-client render envelope:
//   { is, bs, cf, t12, members[] }
export function buildConsolidated(memberPayloads, ttmMonthLabels = []) {
  if (!memberPayloads || memberPayloads.length === 0) return null;
  const memberOutputs = memberPayloads.map((mp) => {
    const accounts = mp.accounts || [];
    const mapping = mp.mapping || {};
    const qboMeta = mp.qboMeta || {};
    const is = buildIncomeStatement(accounts, mapping, {
      plPeriod: qboMeta.pl_period,
      plPeriodPrior: qboMeta.pl_period_prior,
    });
    const niExact = is?.totals?.netIncome ?? 0;
    const niPriorExact = is?.prior?.netIncome ?? 0;
    const bs = buildBalanceSheet(accounts, mapping,
      bsNetIncomeAnchor(niExact, qboMeta), niPriorExact);
    // Total assets for auto-threshold
    const totalAssets = bs?.totals?.totalAssets ?? 0;
    const cf = buildCashFlow(accounts, mapping, niExact, niPriorExact, {
      niAtPeriodStart: qboMeta.ni_at_period_start ?? null,
      cashAtPeriodStart: qboMeta.cash_at_period_start ?? null,
      totalAssets,
    });
    const t12 = mp.ttm?.detail_lines_monthly
      ? buildT12(accounts, mapping, mp.ttm,
                 ttmMonthLabels.length > 0 ? ttmMonthLabels : (mp.ttm.month_labels || []))
      : (mp.ttm?.accounts_monthly
          ? buildT12(mp.ttm.accounts_monthly, mapping,
                     ttmMonthLabels.length > 0 ? ttmMonthLabels : (mp.ttm.month_labels || []))
          : null);
    return { is, bs, cf, t12, client: mp.client, mapping, accounts };
  });

  return {
    is:  mergeIncomeStatements(memberOutputs.map((m) => m.is)),
    bs:  mergeBalanceSheets(memberOutputs.map((m) => m.bs)),
    cf:  mergeCashFlows(memberOutputs.map((m) => m.cf)),
    t12: mergeT12s(memberOutputs.map((m) => m.t12).filter(Boolean), ttmMonthLabels),
    members: memberOutputs,
  };
}

// Validation ties for a consolidated package. Each member's own ties are
// enforced at builder construction time (Rule 4); consolidated ties verify
// that the merge preserved the identity.
export function runConsolidatedValidationTies(consolidated) {
  const ties = [];
  if (!consolidated) return { allGreen: false, ties };
  const { bs, is, cf } = consolidated;

  // BS balances
  const bsDelta = Math.abs((bs?.totals?.totalAssets || 0) - (bs?.totals?.totalLE || 0));
  ties.push({
    id: 'consolidated_bs_balance',
    label: 'Consolidated Balance Sheet balances (A = L + E)',
    ok: bsDelta < 1,
    detail: bsDelta < 1 ? null : `Off by $${bsDelta.toFixed(2)}`,
  });

  // NI from IS == NI on BS
  const isNI = is?.totals?.netIncome || 0;
  const bsNI = bs?.totals?.netIncome || 0;
  ties.push({
    id: 'consolidated_ni_equity_tie',
    label: 'Net Income flows into Equity (consolidated)',
    ok: Math.abs(isNI - bsNI) < 1,
    detail: Math.abs(isNI - bsNI) < 1 ? null : `IS NI=${isNI}, BS NI=${bsNI}`,
  });

  // CF identity (must be exact)
  const opT = cf?.sections?.operating?.subtotal || 0;
  const invT = cf?.sections?.investing?.subtotal || 0;
  const finT = cf?.sections?.financing?.subtotal || 0;
  const othT = cf?.sections?.other?.subtotal || 0;
  const sectionsNet = opT + invT + finT + othT;
  const cashDelta = (cf?.endCash || 0) - (cf?.beginCash || 0);
  const cfDelta = Math.abs(sectionsNet - cashDelta);
  ties.push({
    id: 'consolidated_cf_identity',
    label: 'Consolidated CF: ΣOp + ΣInv + ΣFin = ΔCash',
    ok: cfDelta < 1,
    detail: cfDelta < 1 ? null : `Off by $${cfDelta.toFixed(2)} (sections=${sectionsNet}, ΔCash=${cashDelta})`,
  });

  // CF NI == IS NI (consolidated)
  const cfNI = cf?.netIncome || 0;
  ties.push({
    id: 'consolidated_cf_ni_ties_to_is',
    label: 'Consolidated CF Net Income == IS Net Income',
    ok: Math.abs(cfNI - isNI) < 1,
    detail: Math.abs(cfNI - isNI) < 1 ? null : `CF NI=${cfNI}, IS NI=${isNI}`,
  });

  return { allGreen: ties.every((t) => t.ok), ties };
}

export const ENGAGEMENT_REPORTS = {
  preparation: {
    label: 'Preparation Engagement (AR-C 70)',
    footer: 'No assurance is provided on these financial statements.',
    cover_title: 'Financial Statements',
    accountants_report_required: false, // AR-C 70 has no accountant's report;
    // disclaimer ("No assurance is provided") appears on each statement
    interim_label: 'Interim — Unaudited',
  },
  compilation: {
    label: 'Compilation Engagement (AR-C 80)',
    footer: "See accountant's compilation report.",
    cover_title: 'Compiled Financial Statements',
    accountants_report_required: true,
    interim_label: 'Interim — Unaudited',
  },
  internal: {
    label: 'Internal Management Use Only',
    footer: 'For internal management use only. Not for distribution.',
    cover_title: 'Internal Financial Statements',
    accountants_report_required: false,
    interim_label: 'Internal Working File',
  },
};

// Framework labels (drive non-GAAP statement titles per AR-C 80.A40 and the
// "applicable financial reporting framework" phrase in the compilation
// report wording). Keep these in sync with the ClientCreate.framework
// allowed values in backend/server.py.
export const FRAMEWORKS = {
  gaap: {
    label: 'accounting principles generally accepted in the United States of America',
    short: 'GAAP',
    // "Prepared in accordance with..." phrase used in the AR-C 70 footer
    // for GAAP. (For non-GAAP frameworks the footer uses the shorter
    // "Prepared on the [framework]" form instead.)
    footer_prepared_phrase: 'Prepared in accordance with accounting principles generally accepted in the United States of America',
    titles: {
      bs: 'Balance Sheet',
      pl: 'Statement of Income',
      cf: 'Statement of Cash Flows',
      bs_py: 'Balance Sheet — Prior Year Comparison',
      pl_py: 'Statement of Income — Prior Year Comparison',
      t12: 'Trailing 12 Months Statement of Income',
    },
  },
  income_tax_basis: {
    label: 'the income tax basis of accounting used by the entity for federal income tax reporting purposes',
    short: 'Income Tax Basis',
    footer_prepared_phrase: 'Prepared on the income tax basis of accounting',
    titles: {
      bs: 'Statement of Assets, Liabilities, and Equity — Income Tax Basis',
      pl: 'Statement of Revenues and Expenses — Income Tax Basis',
      cf: 'Statement of Cash Flows — Income Tax Basis',
      bs_py: 'Statement of Assets, Liabilities, and Equity — Income Tax Basis — Prior Year Comparison',
      pl_py: 'Statement of Revenues and Expenses — Income Tax Basis — Prior Year Comparison',
      t12: 'Trailing 12 Months Statement of Revenues and Expenses — Income Tax Basis',
    },
  },
  cash_basis: {
    label: 'the cash basis of accounting',
    short: 'Cash Basis',
    footer_prepared_phrase: 'Prepared on the cash basis of accounting',
    titles: {
      bs: 'Statement of Assets and Equity — Cash Basis',
      pl: 'Statement of Cash Receipts and Disbursements',
      cf: 'Statement of Cash Flows — Cash Basis',
      bs_py: 'Statement of Assets and Equity — Cash Basis — Prior Year Comparison',
      pl_py: 'Statement of Cash Receipts and Disbursements — Prior Year Comparison',
      t12: 'Trailing 12 Months Statement of Cash Receipts and Disbursements',
    },
  },
  modified_cash_basis: {
    label: 'the modified cash basis of accounting',
    short: 'Modified Cash Basis',
    footer_prepared_phrase: 'Prepared on the modified cash basis of accounting',
    titles: {
      bs: 'Statement of Assets, Liabilities, and Equity — Modified Cash Basis',
      pl: 'Statement of Revenues and Expenses — Modified Cash Basis',
      cf: 'Statement of Cash Flows — Modified Cash Basis',
      bs_py: 'Statement of Assets, Liabilities, and Equity — Modified Cash Basis — Prior Year Comparison',
      pl_py: 'Statement of Revenues and Expenses — Modified Cash Basis — Prior Year Comparison',
      t12: 'Trailing 12 Months Statement of Revenues and Expenses — Modified Cash Basis',
    },
  },
  other: {
    label: 'the financial reporting framework specified in the terms of the engagement',
    short: 'Other',
    // For Other, the footer/title use the engagement's free-text
    // framework_other_label. These templates use {label} as the
    // substitution point, handled in getStatementTitle / footer code.
    footer_prepared_phrase: 'Prepared on the {label}',
    titles: {
      bs: 'Statement of Financial Position — {label}',
      pl: 'Statement of Operations — {label}',
      cf: 'Statement of Cash Flows — {label}',
      bs_py: 'Statement of Financial Position — {label} — Prior Year Comparison',
      pl_py: 'Statement of Operations — {label} — Prior Year Comparison',
      t12: 'Trailing 12 Months Statement of Operations — {label}',
    },
  },
  // V18 — AR-C 70 .A6 path. Titles drop framework modifiers; footer
  // text is replaced with the AR-C 70.A6 disclosure verbatim. Only
  // valid for PREPARED engagements (AR-C 80 compilation reports
  // require a framework reference).
  none: {
    label: 'no applicable financial reporting framework',
    short: 'No Framework',
    footer_prepared_phrase:
      'No assurance is provided on these financial statements. The financial statements have not been prepared in accordance with an applicable financial reporting framework, and accordingly may not be appropriate for users who are not informed about the basis of accounting used.',
    titles: {
      bs: 'Balance Sheet',
      pl: 'Statement of Income',
      cf: 'Statement of Cash Flows',
      bs_py: 'Balance Sheet — Prior Year Comparison',
      pl_py: 'Statement of Income — Prior Year Comparison',
      t12: 'Trailing 12 Months Statement of Income',
    },
  },
};

// Get the statement title for the given framework + statement id, with a
// safe fallback to GAAP titles if the framework is unrecognized. When the
// framework is "other", the engagement's free-text label is substituted
// into the {label} placeholder.
export function getStatementTitle(stmtId, framework = 'gaap', frameworkOtherLabel = '') {
  const f = FRAMEWORKS[framework] || FRAMEWORKS.gaap;
  const raw = f.titles[stmtId] || FRAMEWORKS.gaap.titles[stmtId] || stmtId;
  if (framework === 'other') {
    const lbl = (frameworkOtherLabel || '').trim() || 'Other Comprehensive Basis';
    return raw.replace(/\{label\}/g, lbl);
  }
  return raw;
}

// V19 — Effective framework for rendered output.
//
// On PREPARED engagements (AR-C 70), Steele Martin Jones does NOT disclose
// the financial reporting basis on the rendered package. Per V19 spec,
// the framework dropdown on Engagement Settings is retained for internal
// tracking but does not drive any rendered output. Every prepared
// engagement renders with the AR-C 70.A6 disclosure regardless of the
// stored `client.framework` value.
//
// On COMPILATION engagements (AR-C 80), the framework is part of the
// required report wording — return `client.framework` unchanged so the
// V17 compilation behavior is preserved.
//
// Single source of truth — call this everywhere the rendered framework
// is needed. Do NOT read `client.framework` directly in render code.
export function getEffectiveFramework(client) {
  if (!client) return 'gaap';
  const engagementType = client.engagement_type || 'preparation';
  if (engagementType === 'compilation') {
    return client.framework || 'gaap';
  }
  // Preparation engagements always render with AR-C 70.A6 no-framework
  // disclosure. Framework dropdown is recorded but not disclosed.
  return 'none';
}

// Framework's "applicable financial reporting framework" phrase, used
// verbatim in the AR-C 80 compilation report.
export function getFrameworkPhrase(framework = 'gaap', frameworkOtherLabel = '') {
  const base = (FRAMEWORKS[framework] || FRAMEWORKS.gaap).label;
  if (framework === 'other') {
    const lbl = (frameworkOtherLabel || '').trim();
    return lbl ? `the ${lbl}` : base;
  }
  return base;
}

// Footer phrase for the AR-C 70 ("Prepared on the / Prepared in
// accordance with [framework]") segment. Item 4, 2026-05-21 push.
export function getFooterFrameworkPhrase(framework = 'gaap', frameworkOtherLabel = '') {
  const f = FRAMEWORKS[framework] || FRAMEWORKS.gaap;
  const tmpl = f.footer_prepared_phrase;
  if (framework === 'other') {
    const lbl = (frameworkOtherLabel || '').trim() || 'other comprehensive basis of accounting';
    return tmpl.replace(/\{label\}/g, lbl);
  }
  return tmpl;
}

// Short framework-only segment for AR-C 80 non-GAAP footer
// ("· Prepared on the income tax basis of accounting").
export function getCompiledFooterFrameworkPhrase(framework = 'gaap', frameworkOtherLabel = '') {
  if (framework === 'gaap') return null; // Compiled + GAAP footer is just "See accountant's compilation report."
  return getFooterFrameworkPhrase(framework, frameworkOtherLabel);
}

// Decide whether the package qualifies as "interim" based on period dates.
// Interim = period does not cover a full fiscal year (Jan 1 - Dec 31).
export function isInterimPeriod(periodStart, periodEnd) {
  if (!periodStart || !periodEnd) return true;
  try {
    const s = new Date(periodStart);
    const e = new Date(periodEnd);
    const fullYear =
      s.getMonth() === 0 && s.getDate() === 1 &&
      e.getMonth() === 11 && e.getDate() === 31 &&
      s.getFullYear() === e.getFullYear();
    return !fullYear;
  } catch {
    return true;
  }
}

// Reporting period helpers — backend stamps period_start / period_end on each
// /api/qbo/accounts response; consumers should always use those values when
// available so the label matches the data.
//
// V15.2: when `qboMeta` is supplied, derive the period range from
// `period_start` / `period_end` so the labels reflect the engagement's
// reporting period (e.g. "Four Months Ended April 30, 2026") instead of
// the trailing-month default. The bare `defaultPeriodLabels()` call still
// returns trailing-month labels for legacy callers.
export function defaultPeriodLabels(qboMeta = null) {
  const MONTHS = ['January','February','March','April','May','June',
                  'July','August','September','October','November','December'];
  const fmt = (d) => `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  const monthsBetween = (a, b) => {
    // Count whole calendar months from `a` to `b` inclusive of partials.
    // For Jan 1 → Apr 30, returns 4.
    return (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1;
  };
  const MONTH_WORDS = ['', 'One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve'];

  let periodStart, periodEnd;
  if (qboMeta && qboMeta.period_end) {
    periodEnd = new Date(qboMeta.period_end + 'T12:00:00');
    if (qboMeta.period_start) periodStart = new Date(qboMeta.period_start + 'T12:00:00');
  } else {
    const today = new Date();
    periodEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    periodStart = new Date(periodEnd.getFullYear(), 0, 1);
  }
  if (!periodStart) periodStart = new Date(periodEnd.getFullYear(), 0, 1);

  const dateLabel = fmt(periodEnd);
  const year = periodEnd.getFullYear();
  // "Four Months Ended April 30, 2026" — used on cover page + statements.
  // Fall back to "[Date] YTD" wording when months don't compute cleanly.
  const nMonths = monthsBetween(periodStart, periodEnd);
  let monthsEndedLabel;
  if (nMonths === 12) {
    monthsEndedLabel = `Year Ended ${dateLabel}`;
  } else if (nMonths >= 1 && nMonths <= 12) {
    monthsEndedLabel = `${MONTH_WORDS[nMonths]} Months Ended ${dateLabel}`;
  } else {
    monthsEndedLabel = `Period Ended ${dateLabel}`;
  }
  // YTD-style "For the period ..." sentence retained for IS / CF headers.
  const forPeriodLabel = nMonths === 12
    ? `For the year ended ${dateLabel}`
    : `For the period ${fmt(periodStart)} through ${dateLabel}`;

  // Dual-period CPA convention for the IS PY / BS PY comparison header.
  // 4-month YTD example:  "For the Four Months Ended April 30, 2026 and 2025"
  // 12-month example:     "For the Years Ended April 30, 2026 and 2025"
  // Fallback (irregular):  "For the period ended {date_label} and {prior_year}"
  // Drop the "(date), 2025" form deliberately — comparative dates are
  // implicit because PY column is always the SAME date in the prior year.
  const dateNoYearLabel = `${MONTHS[periodEnd.getMonth()]} ${periodEnd.getDate()}`;
  let dualPeriodLabel;
  if (nMonths === 12) {
    dualPeriodLabel = `For the Years Ended ${dateNoYearLabel}, ${year} and ${year - 1}`;
  } else if (nMonths >= 1 && nMonths <= 12) {
    dualPeriodLabel = `For the ${MONTH_WORDS[nMonths]} Months Ended ${dateNoYearLabel}, ${year} and ${year - 1}`;
  } else {
    dualPeriodLabel = `For the period ended ${dateLabel} and ${year - 1}`;
  }

  // Lowercase, single-period prepositional form. Used in compilation-report
  // prose where the prefix "for the" is already in the template, so we
  // need "four months ended April 30, 2026" (no leading "For the") to
  // avoid the V16 double-"ended" bug ("for the period ended Four Months
  // Ended April 30, 2026"). 12-month: "year ended April 30, 2026".
  const MONTH_WORDS_LC = ['', 'one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve'];
  let singlePeriodLowercaseLabel;
  if (nMonths === 12) {
    singlePeriodLowercaseLabel = `year ended ${dateLabel}`;
  } else if (nMonths >= 1 && nMonths <= 12) {
    singlePeriodLowercaseLabel = `${MONTH_WORDS_LC[nMonths]} months ended ${dateLabel}`;
  } else {
    singlePeriodLowercaseLabel = `period ended ${dateLabel}`;
  }

  return {
    asOfLabel: `As of ${dateLabel}`,
    forPeriodLabel,
    monthsEndedLabel,
    dualPeriodLabel,
    singlePeriodLowercaseLabel,
    ytdLabel: `YTD ${year}`,
    priorYtdLabel: `YTD ${year - 1}`,
    reportingYear: year,
    priorYear: year - 1,
    periodStart,
    periodEnd,
    monthsCount: nMonths,
  };
}
